import type { PaginatedReport } from '../report-pagination.ts';
/**
 * API data-transfer types shared by the Worker and the browser.
 *
 * These are the camelCase shapes that cross the wire. The snake_case row shapes
 * that come out of the database live in `src/worker/database/types.ts`.
 */

import type { FeedbackCategory, FeedbackStatus } from '../constants/feedback.ts';
import type { LocalizedText } from '../constants/localized-text.ts';
import type { ChecklistItemStatus } from '../checklists/cadence.ts';
import type { TemplateTags } from '../constants/template-taxonomy.ts';
import type { SupportedLanguage } from '../constants/languages.ts';
import type { DateFormatStyle, TimeFormatStyle } from '../locale-format.ts';
import type { CancelledSlotAudience } from '../cancelled-slots.ts';
import type { PublicDemoState } from '../public-demo.ts';
import type { QuotePaymentSummary } from '../calculations/payments.ts';
import type { QuoteRequestPhotoIntake } from './photo-quotes.ts';
import type { CallLineProblemReason } from '../voice.ts';
import type {
  AppointmentPaymentStatus,
  AppointmentStatus,
  BillingBasis,
  BillingInterval,
  BillingMode,
  BillingStatus,
  DiscountType,
  FloorType,
  InvoiceStatus,
  OAuthProvider,
  OccupancyStatus,
  PaymentMethodFamily,
  PaymentMethodKind,
  PaymentStatus,
  PaymentType,
  QuotePaymentStatus,
  ChangeOrderStatus,
  ExtraWorkVisitBlockedReason,
  ComplianceDocumentKind,
  InspectionStatus,
  RestockStatus,
  ServiceRequestKind,
  ServiceRequestStatus,
  SiteAccessKind,
  MeasurementBasis,
  NotificationType,
  Permission,
  PlanDefinition,
  PlanFeature,
  PlanId,
  PropertyType,
  QuoteDeclineReason,
  QuoteRequestFrequency,
  QuoteRequestSource,
  QuoteRequestStatus,
  QuoteSegment,
  QuoteStatus,
  PhoneLineTypeCode,
  RoomConditionCode,
  RoomSizeCode,
  SeriesFrequency,
  SeriesStatus,
  ServiceFrequency,
  ServiceMode,
  SmsConsentSource,
  SmsConsentStatus,
  SubscriptionCapabilities,
  UserRole,
  PayType,
  WorkerType,
} from '../constants/index.ts';
import type { WidgetSettings } from '../widget.ts';
import type { AiSettings } from '../ai.ts';
import type { FrequencyTierSettings } from '../frequency-tiers.ts';
import type {
  CaptureStatus,
  CaptureType,
  MagicQuoteAiStatus,
  MagicQuoteCustomerDraft,
  MagicQuoteMode,
  MagicQuoteStatus,
  ObservationReviewStatus,
  ObservationType,
  ObservationVisibility,
} from '../magic-quote.ts';
import type { ApiKeyPreset, ApiScope } from '../constants/api-keys.ts';

// ---------------------------------------------------------------------------
// Envelopes (spec §10.1, §10.2)
// ---------------------------------------------------------------------------

export interface ApiSuccess<T> {
  data: T;
  requestId: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
  requestId: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface SessionUser {
  id: string;
  companyId: string;
  email: string;
  fullName: string;
  /**
   * Optional and unverified — the default for "text this to me" and nothing
   * else. Nothing is authorized by it and no message is ever sent to it
   * without somebody choosing to.
   */
  phone: string | null;
  role: UserRole;
  permissions: Permission[];
  lastLoginAt: string | null;
  /**
   * False for an account created through a social provider that has never set
   * one. Settings uses it to offer "Set a password" instead of asking for a
   * current password nobody has, and the connected-accounts section uses it to
   * decide whether the last provider may be disconnected.
   */
  hasPassword: boolean;
}

/**
 * A social account connected to the signed-in user (GET /auth/identities).
 * Only what Settings needs to name it — the provider's tokens are never stored
 * and its subject id never leaves the Worker.
 */
export interface LinkedIdentity {
  provider: OAuthProvider;
  email: string | null;
  displayName: string | null;
  linkedAt: string;
  lastLoginAt: string | null;
}

/**
 * The provider-verified identity waiting for a company name, as the
 * finish-signing-up page sees it (GET /auth/oauth/pending). The account does
 * not exist yet; this is what will be used to create it.
 */
export interface PendingOAuthSignup {
  provider: OAuthProvider;
  email: string;
  fullName: string;
}

/**
 * What a sign-in answers with when the password was right but a second factor
 * is still owed.
 *
 * A distinct shape rather than a `SessionResponse` with empty fields, so the
 * front end cannot accidentally treat a half-finished sign-in as a session —
 * the type system refuses it.
 */
export interface MfaChallengeResponse {
  mfaRequired: true;
  /**
   * Signed, short-lived, and good for exactly one thing: finishing this
   * sign-in.
   *
   * Absent when the sign-in came back from a social provider: there was no
   * form to hold it across the redirect, so it travels in an HttpOnly cookie
   * the browser cannot read and the server supplies it. See
   * `services/auth/pending-login.ts`.
   */
  loginToken?: string;
  options: { methodId: string | null; type: 'totp' | 'sms' | 'webauthn' | 'recovery'; label: string }[];
  trustedDeviceDays: number;
}

export type LoginResponse = SessionResponse | MfaChallengeResponse;

/** Narrowing helper, so callers do not test the marker property by hand. */
export function isMfaChallenge(response: LoginResponse): response is MfaChallengeResponse {
  return 'mfaRequired' in response && response.mfaRequired === true;
}

export interface SessionResponse {
  user: SessionUser;
  company: Company;
  /**
   * The location this session is working in, and every location the user may
   * switch into. `location` is null only for a user with no active location
   * assignment — a state the session loader heals for owners and the team
   * page fixes for everyone else.
   */
  location: LocationSummary | null;
  locations: LocationSummary[];
  csrfToken: string;
  /**
   * Whether this deployment sells an app subscription. False on environments
   * without Stripe configuration — where access still expires (see
   * `demoMode`), but no Subscribe button can lead anywhere.
   */
  billingEnforced: boolean;
  /**
   * Whether this deployment is a demo: no billing configured, a short access
   * window, and nothing here fit to run a business on. The client shows the
   * warning banner and the expiry countdown from this.
   */
  demoMode: boolean;
  /** Days a demo account gets from signup. Only meaningful with `demoMode`. */
  demoExpirationDays: number;
  /**
   * Present only inside the SHARED PUBLIC DEMO — the one company anybody can
   * walk into from the marketing site — and null for every other session.
   *
   * Not to be confused with `demoMode` above, which is a property of the
   * DEPLOYMENT (no billing configured, every company gets a few days). This is
   * a property of the SESSION: one company, many strangers, a credit budget
   * per visitor and a rebuild every week. The app draws its demo banner, its
   * credit meter and its countdown from this and from nothing else.
   */
  publicDemo?: PublicDemoState | null;
  /**
   * The operator's email when this session was minted from /admin's "sign in
   * as this user", absent or null for the ordinary sign-ins that are every
   * other session. The app renders a standing banner from it, so that nobody
   * — the operator least of all — can forget whose account they are in.
   */
  impersonatedBy?: string | null;
  /**
   * Set when this deployment requires two-factor authentication and this
   * account has nothing enrolled.
   *
   * The session is real — refusing to issue one would lock every existing user
   * out the moment the mandate is switched on, which is why the server admits
   * them and walks them into enrollment instead. This flag is how it asks the
   * client to do the walking; a client that ignored it would leave the mandate
   * with no effect at all for password sign-ins.
   */
  mfaSetupRequired?: boolean;
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

/** What the location switcher needs — nothing more. */
export interface LocationSummary {
  id: string;
  name: string;
  isPrimary: boolean;
  status: LocationStatus;
  /**
   * This location's effective timezone (its own override, or the company's
   * if it has none) — the same resolution `effectiveLocationSettings` does
   * server-side. Scheduling views should derive "today" from the session's
   * current location's timezone, not the company's, since a location may
   * override it.
   */
  timezone: string;
  /**
   * This location's effective hourly rate (its own override, or the company's
   * if it has none), resolved the same way `timezone` above is. The booking
   * form prices a visit with it when there is no quote to price it from, and a
   * branch that charges more per hour must not quote the head office's rate.
   */
  defaultHourlyRateCents: number;
  /**
   * This location's effective currency (its own, or the company's), which
   * `defaultHourlyRateCents` above is denominated in. Optional only because a
   * cached session from before 0114 does not carry it; read it through
   * `currencyCode ?? company currency`.
   */
  currencyCode?: string;
}

export type LocationStatus = 'active' | 'archived';

/**
 * A company location (GET /api/v1/locations). Every field in `overrides` is
 * null when the location inherits the company-wide setting; `effective` is
 * the resolved result the location actually operates with.
 */
export interface Location {
  id: string;
  name: string;
  isPrimary: boolean;
  status: LocationStatus;
  /** Why an archived location is archived: locked by a downgrade, or by hand. */
  archivedReason: 'downgrade' | 'manual' | null;
  archivedAt: string | null;
  /** This location's own public quote-request form (/r/{token}); null = none. */
  intakePath: string | null;
  overrides: LocationOverrides;
  effective: LocationEffectiveSettings;
  createdAt: string;
  updatedAt: string;
}

/** The company settings a location may override. Null = inherit. */
export interface LocationOverrides {
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  timezone: string | null;
  defaultHourlyRateCents: number | null;
  defaultQuoteExpirationDays: number | null;
  taxEnabled: boolean | null;
  defaultTaxRateBasisPoints: number | null;
  laborIsTaxable: boolean | null;
  billingIncrementMinutes: number | null;
  quoteNumberPrefix: string | null;
  invoiceNumberPrefix: string | null;
  defaultTerms: string | null;
  appointmentConfirmLeadHours: number | null;
  appointmentReminderLeadHours: number | null;
  smsSenderLabel: string | null;
  /** This branch's working window; null follows the company's. */
  workDayStartLocal: string | null;
  workDayEndLocal: string | null;
  /** Comma-separated weekday numbers, 0 = Sunday. */
  workDays: string | null;
  /** Whether this branch offers tips; null follows the company's answer. */
  tipsEnabled: boolean | null;
  /** Whether a late cancellation fee applies here; null follows the company. */
  lateCancelFeeEnabled: boolean | null;
  /** Hours of notice required; null follows the company. */
  lateCancelNoticeHours: number | null;
  /** Fee in minor units; null follows the company. */
  lateCancelFeeCents: number | null;
  /** Whether a canceled spot is offered to customers who want an earlier time. Null follows the company. */
  autoOfferCancelledSlots: boolean | null;
  /** Hours of notice before a spot is offered. Null follows the company. */
  cancelledSlotNoticeHours: number | null;
  /** Hours to wait before the next group is told. Null follows the company. */
  cancelledSlotRoundHours: number | null;
  /** How many people hear about the spot at once. Null follows the company. */
  cancelledSlotRoundSize: number | null;
  /** Quiet hours start, as a 24-hour clock hour. Null follows the company. */
  cancelledSlotQuietStartHour: number | null;
  /** Quiet hours end, as a 24-hour clock hour. Null follows the company. */
  cancelledSlotQuietEndHour: number | null;
  /** Whether the office must approve a move before it happens. Null follows the company. */
  cancelledSlotRequireApproval: boolean | null;
  /** This branch's own currency (ISO 4217); null follows the company's. */
  currencyCode: string | null;
}

/** The overrides resolved against the company: what the location works with. */
export interface LocationEffectiveSettings {
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  timezone: string;
  defaultHourlyRateCents: number;
  defaultQuoteExpirationDays: number;
  taxEnabled: boolean;
  defaultTaxRateBasisPoints: number;
  laborIsTaxable: boolean;
  billingIncrementMinutes: number;
  quoteNumberPrefix: string;
  invoiceNumberPrefix: string;
  defaultTerms: string | null;
  appointmentConfirmLeadHours: number;
  appointmentReminderLeadHours: number;
  smsSenderLabel: string | null;
  workDayStartLocal: string;
  workDayEndLocal: string;
  /** Comma-separated weekday numbers, 0 = Sunday. */
  workDays: string;
  tipsEnabled: boolean;
  /** Late cancellation fee, resolved against the company. */
  lateCancelFeeEnabled: boolean;
  lateCancelNoticeHours: number;
  lateCancelFeeCents: number;
  /** Filling a canceled spot, resolved against the company. */
  autoOfferCancelledSlots: boolean;
  cancelledSlotNoticeHours: number;
  cancelledSlotRoundHours: number;
  cancelledSlotRoundSize: number;
  cancelledSlotQuietStartHour: number;
  cancelledSlotQuietEndHour: number;
  cancelledSlotRequireApproval: boolean;
  /** The currency new quotes, invoices and fees at this location are written in. */
  currencyCode: string;
}

// ---------------------------------------------------------------------------
// Company
// ---------------------------------------------------------------------------

export interface Company {
  cancelledSlotTeamScope?: 'same_team' | 'any_eligible';
  areaPricingRules?: import('../area-pricing.ts').AreaPricingRules | null;
  dustPolicy?: import('../dust-evidence.ts').DustPolicy | null;
  walkthroughPolicy?: import('../walkthrough-gate.ts').WalkthroughPolicy | null;
  walkthroughPolicyRevision?: number;
  id: string;
  name: string;
  legalName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string;
  logoUrl: string | null;
  currencyCode: string;
  timezone: string;
  /**
   * The language this company's outbound email and texts are written in. Not
   * the language of the app's interface, which each user's browser decides.
   */
  language: string;
  /**
   * BCP 47 locale for amounts and dates (en-US, en-ZA). Null means this
   * company has never chosen one, so the web app keeps the browser's format
   * and documents keep the language's locale — what every existing company
   * already shows. en-US is what a new signup stores.
   */
  locale: string | null;
  /** short / medium / long, or null to follow `locale`. */
  dateFormat: DateFormatStyle | null;
  /** 12-hour or 24-hour, or null to follow `locale`. */
  timeFormat: TimeFormatStyle | null;
  /**
   * What kind of cleaning this company does. Decides which starter libraries
   * it carries and what a new quote defaults to; under 'both', each quote
   * asks. Existing companies read 'residential', the app they signed up for.
   */
  serviceMode: ServiceMode;
  /**
   * When the question above was actually answered — at signup, or by saving
   * the company settings card. Null = never, so the mode beside it is the
   * historical default rather than anybody's choice, which is what the
   * settings setup guide lists as a step still to do.
   */
  serviceModeChosenAt: string | null;
  /**
   * Minutes after a visit's start with nobody clocked in before the owner is
   * alerted. Null = the alerts are off, which is every company's default.
   */
  missedClockInAlertMinutes: number | null;
  /** Opt-in: every active assigned staff member must have a punch before completion. */
  requireClockInToComplete?: boolean;
  blockCompletionOnMissingPhotos?: boolean;
  /** Shift swaps (0095). */
  swapPolicy?: import('../shift-swaps.ts').SwapPolicy;
  swapCutoffHours?: number;
  /**
   * What an hour of crew time costs, loaded. Null = not recorded, and job
   * costing reports revenue and hours without a margin rather than inventing
   * a cost and calling the answer one.
   */
  laborCostCentsPerHour: number | null;
  /**
   * Hours the company promises to acknowledge a client's complaint in. Null =
   * no promise is made, and the portal shows no target.
   */
  responseTargetHours: number | null;
  defaultHourlyRateCents: number;
  defaultQuoteExpirationDays: number;
  taxEnabled: boolean;
  defaultTaxRateBasisPoints: number;
  laborIsTaxable: boolean;
  billingIncrementMinutes: number;
  quoteNumberPrefix: string;
  invoiceNumberPrefix: string;
  defaultTerms: string | null;
  showTimeOnQuote: boolean;
  showHourlyRateOnQuote: boolean;
  recurringHorizonMonths?: 3 | 6 | 9;
  appointmentConfirmLeadHours: number;
  /** Working hours (0059): HH:MM local plus a comma list of weekday numbers. */
  workDayStartLocal: string;
  workDayEndLocal: string;
  workDays: string;
  appointmentReminderLeadHours: number;
  /** Whether this company sends text reminders at all. Off until switched on. */
  smsRemindersEnabled: boolean;
  /** This company's ceiling on texts per rolling 24 hours. */
  smsDailyLimit: number;
  /** Short name texts are sent under; falls back to `name`. */
  smsSenderLabel: string | null;
  /**
   * Whether a text could actually be sent for this company — from the
   * deployment's Twilio account or from one this company connected itself.
   *
   * Served by every endpoint that answers with the signed-in company —
   * `GET /company`, and the session payloads from `/auth/session`, `/auth/login`,
   * `/auth/register` and `/auth/oauth/complete-signup` — since both UI readers
   * take it from the session rather than fetching the company separately.
   * Endpoints that serialize a company row on its own still omit it, so
   * undefined means "not asked", not "no": callers fall back to the
   * deployment-wide `smsAvailable` from `GET /config`.
   */
  smsConfigured?: boolean;
  /** Days after sending before an unanswered quote gets a nudge. 0 = off. */
  quoteFollowUpDays: number;
  /** Days before its expiration date a customer is warned. 0 = off. */
  quoteExpiryWarningDays: number;
  /** Whether staff are emailed when a customer views/accepts/declines a quote. */
  notifyOnQuoteEvents: boolean;
  /** Whether owners are alerted when a customer asks to move a visit. */
  notifyOwnersOnChangeRequest: boolean;
  /** Whether the cleaner assigned to that visit is alerted alongside them. */
  notifyAssigneeOnChangeRequest: boolean;
  /** Whether those alerts also go out by text, on top of the email. */
  textChangeRequestAlerts: boolean;
  /**
   * Whether owners and managers are told when a visit is canceled.
   * Email, the in-app alert, and a text when texting is set up.
   */
  notifyOwnersOnVisitCancelled: boolean;
  /** Whether each invoice is emailed to the customer once its visit has started. */
  emailInvoicesToCustomers: boolean;
  /**
   * Whether the customer is emailed a receipt automatically when a payment is
   * recorded or settles. On unless switched off; the office can still email
   * one from the payment.
   */
  emailReceiptsToCustomers: boolean;
  /** Whether that invoice notice also goes out by text, on top of the email. */
  textInvoicesToCustomers: boolean;
  /**
   * Whether customers are offered a tip when paying. Off unless switched on;
   * locations may override it either way (see LocationOverrides.tipsEnabled).
   */
  tipsEnabled: boolean;
  /**
   * Whether canceling a visit inside the notice window can charge a fee.
   * Off unless switched on. The hours and the amount below are still stored
   * while it is off, so turning it on starts from 24 hours and the default fee.
   */
  lateCancelFeeEnabled: boolean;
  /** How much notice avoids the fee, in hours. */
  lateCancelNoticeHours: number;
  /** The fee, in minor units of the company currency. */
  lateCancelFeeCents: number;
  /**
   * Whether a canceled visit's time is offered to customers who asked to
   * come earlier. Off unless switched on. Works on every plan.
   */
  autoOfferCancelledSlots: boolean;
  /** How much notice a spot needs before it is offered, in hours. */
  cancelledSlotNoticeHours: number;
  /** How long to wait before telling the next group, in hours. */
  cancelledSlotRoundHours: number;
  /** How many people hear about one open spot at a time. */
  cancelledSlotRoundSize: number;
  /** No new group goes out from this hour (0–23) until the end hour. */
  cancelledSlotQuietStartHour: number;
  /** A group that waited overnight goes out at this hour (0–23). */
  cancelledSlotQuietEndHour: number;
  /** When on, the office approves the move before the visit changes. */
  cancelledSlotRequireApproval: boolean;
  /** Who hears about an open spot: the canceled visit's team only, or anyone eligible. */
  cancelledSlotOfferAudience: CancelledSlotAudience;
  /**
   * Path of the public quote-request form (e.g. /r/{token}), or null when the
   * form has not been enabled. The path is public by design — companies link
   * it from their own websites — so unlike share tokens it is re-displayable.
   */
  intakePath: string | null;
  /**
   * Style options for the embeddable website widget and the hosted form.
   * Always complete — defaults are filled in server-side — so the settings
   * screen edits it directly and never needs a "not set yet" state.
   */
  widgetSettings: WidgetSettings;
  /** The AI assist toggles, complete for the same reason. */
  aiSettings: AiSettings;
  /** Frequency pricing tier defaults (migration 0110), complete for the same reason. */
  frequencyTierSettings: FrequencyTierSettings;
  /**
   * Whether this DEPLOYMENT has a model provider configured at all.
   *
   * Deliberately separate from `aiSettings.enabled`, which is the company's
   * own choice: a toggle that is on against a deployment with no key is a
   * promise nothing can keep, and the screens that offer AI work need to be
   * able to say so rather than letting people start something that will
   * refuse at the far end.
   */
  aiConfigured: boolean;
  /**
   * Whether this company can actually start a Magic Quote walkthrough.
   *
   * Computed server-side from all four things that have to agree — the plan
   * carries the feature, the deployment has a provider, and both AI switches
   * are on — because a client deriving it from the parts gets to be wrong in
   * a way that sends somebody into a customer's house with a camera.
   */
  magicQuoteAvailable: boolean;
  /**
   * Which gate is closed, when one is — null exactly when the above is true.
   *
   * Carried so a client can say something an owner can act on. `plan` is the
   * only one with a purchase behind it; `ai_off` and `feature_off` are
   * switches in Settings; `unconfigured` is the deployment's own missing
   * model key, which nobody using the app can do anything about. Telling all
   * four to buy an upgrade, as a lone boolean forced, is wrong three times
   * out of four.
   */
  magicQuoteUnavailableReason: 'plan' | 'ai_off' | 'feature_off' | 'unconfigured' | null;
  /**
   * Whether the quotes this company sends carry the app's "Powered by …"
   * line — on the public page, in the PDF and at the foot of the email.
   *
   * True for every company until somebody turns it off, and only a plan
   * carrying `unbranded_quotes` may: on the Free plan the switch is refused,
   * which is what that plan costs instead of money.
   */
  quoteBrandingEnabled: boolean;
  /** Whether accepting a quote requires the customer's explicit signature (migration 0077). */
  requireQuoteSignature: boolean;
  /** Customer quote acceptance can book only when explicitly enabled. */
  quoteInstantBookingEnabled: boolean;
  /** Where post-visit review requests point; null = requests never send. */
  reviewRequestUrl: string | null;
  /** Days of Magic Quote media retention; null keeps it indefinitely. */
  magicQuoteRetentionDays: number | null;
  /**
   * Whether this deployment shows this company photo quotes at all (the
   * `FEATURE_PHOTO_DUMP` flag). The plan still decides how much of them:
   * the upload page with `online_intake`, the review queue with `magic_quote`,
   * the sample house on any plan.
   */
  photoQuotesEnabled: boolean;

  // App subscription — enough for the UI to show trial state and gate
  // plan-tier features everywhere; the full picture (seats, locations,
  // prices, portal access) comes from GET /billing.
  //
  // The mobile app gates from these fields. After an owner on a live trial
  // switches to Free, `plan` and `effectivePlan` are `free`, `planFeatures`
  // is `['quote_payments']` (what every plan carries), `trialEndsAt` is the
  // moment the trial ended (null on a
  // `trialing` company still means the trial has no deadline and is
  // running), and `billingStatus` is `canceled` so the trial is not running.
  trialEndsAt: string | null;
  billingStatus: BillingStatus;
  billingPeriodEnd: string | null;
  /** The chosen plan (null while trialing before a choice; `free` once chosen). */
  plan: PlanId | null;
  /**
   * The plan whose features apply right now. A live trial is the top plan.
   * After the owner ends that trial by choosing Free, this is `free`.
   */
  effectivePlan: PlanId;
  /**
   * The features that plan carries, as the server computed them.
   *
   * Redundant for a client that can read the shared catalog (the web app
   * imports `PLANS` from the same repository), and the whole answer for one
   * that cannot: the mobile app would otherwise keep its own copy of the
   * table in Dart and drift from it the first time a tier changed. Same
   * principle as `magicQuoteAvailable` — the question is answered once, by
   * the side that enforces it.
   */
  planFeatures: PlanFeature[];
  /**
   * Whether the Worker's access lock is closed on this company right now
   * (`isSubscriptionLocked`), answered by the side that enforces it.
   *
   * The clients used to derive this from the trial deadline, which stopped
   * being sufficient the moment a Free account could be paused for a year of
   * inactivity: a paused company has no deadline to read, so a client working
   * it out for itself renders an ordinary app whose every request then answers
   * 402. Nobody reaches that state by signing in — a sign-in un-pauses — so in
   * practice this is what an operator sees during a support visit, and what it
   * should show them is the lock screen their customer would be looking at.
   */
  accessLocked: boolean;
  /** Source action workflow is exposed only when its deployment flag is on. */
  inboxActionsAvailable?: boolean;
  /** Whether this installation answers calls (FEATURE_VOICE_INBOX). The plan gate is `voice_inbox`. */
  voiceInboxAvailable?: boolean;
  /**
   * Whether the web app shows Settings as a hub of links with one page per
   * section (FEATURE_SETTINGS_HUB), rather than one long page. Layout
   * only: no setting is gated on it.
   */
  settingsHub?: boolean;
  /**
   * Whether this installation issues BlastoClean-managed texting numbers
   * (FEATURE_MANAGED_SMS, off by default). False: offer only "connect your own
   * Twilio account". A number already issued keeps working either way.
   */
  managedSmsAvailable?: boolean;
  /**
   * Whether the web app shows the Work hub (`/work`) for its secondary office
   * tools (FEATURE_NAV_SECONDARY_HUB, off by default). Navigation only: it
   * grants and withholds no feature.
   */
  navSecondaryHub?: boolean;
  /**
   * Whether Reports opens as a hub of report cards with one report per route
   * (`/reports/:section`) rather than the tabbed page (the
   * `FEATURE_REPORTS_HUB_ROUTES` flag, off unless set).
   */
  reportsHubRoutes?: boolean;
  /**
   * Whether Settings opens templates, team and account on pages of their own
   * (FEATURE_SETTINGS_HUB_ROUTES, on by default). False or absent: those cards
   * stay stacked on /settings. Display only; every gate inside is unchanged.
   */
  settingsHubRoutes?: boolean;
  inboxActionKinds?: Array<'reschedule' | 'visit_instruction' | 'create_quote'>;
  /**
   * WHY the door is shut, when it is: a trial (or demo period) that ran out,
   * or a Free account paused for a year of inactivity. Null when it is open.
   *
   * Sent rather than inferred because the two need different screens — the
   * lapsed-trial one offers "continue on Free", which a paused account
   * already is, so pressing it succeeds and changes nothing. A client that
   * worked the reason out from the other fields would be re-deriving an
   * invariant the server is the only one entitled to state.
   */
  accessLockedReason: 'trial' | 'dormant' | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/v1/billing — the company's subscription, seats, locations and plan. */
export interface BillingState {
  /** False when the deployment has no gateway configured: nothing to sell. */
  configured: boolean;
  /** The back end that bills this deployment: always Stripe. */
  provider: 'stripe';
  /**
   * The inverse of `configured`: this deployment is a demo. Access still
   * expires — sooner, in fact — but there is nothing to subscribe to, so the
   * UI offers an explanation instead of a Subscribe button.
   */
  demoMode: boolean;
  /** Days a demo account gets from signup (app_settings.demo_expiration_days). */
  demoExpirationDays: number;
  status: BillingStatus;
  trialEndsAt: string | null;
  /** Whole days of trial remaining (0 when expired or not trialing). */
  trialDaysLeft: number;
  /** True when the trial is over and there is no live subscription. */
  locked: boolean;
  billingPeriodEnd: string | null;
  /**
   * A cancellation already asked for: the subscription runs to
   * `billingPeriodEnd` and then stops renewing. Stripe's own page shows the
   * same state; this mirrors it for the operator CMS and the cron.
   */
  cancelAtPeriodEnd: boolean;
  /**
   * The plan the company chose (null while trialing before a choice; `free`
   * once an owner chose it), and the plan whose entitlements it currently
   * enjoys. A live trial enjoys the top plan. Choosing Free during that
   * trial ends it, so both are `free` and `trialEndsAt` is the moment it
   * ended. `status` is then `canceled`, not `trialing`.
   */
  plan: PlanId | null;
  effectivePlan: PlanId;
  /**
   * The billing cycle the subscription runs on. 'month' until a yearly
   * checkout or switch says otherwise.
   */
  interval: BillingInterval;
  /**
   * Whether this deployment offers yearly billing at all (the yearly price
   * catalog is configured). The prices are YEARLY_PRICE_MULTIPLIER × the
   * monthly amounts in `plans`.
   */
  yearlyAvailable: boolean;
  /** The full catalog, so the billing page can render every tier. */
  plans: Readonly<Record<PlanId, PlanDefinition>>;
  /** Active users right now — what the seat billing follows. */
  seats: number;
  /** Included seats / prices of the company's CURRENT effective plan. */
  includedSeats: number;
  basePriceCents: number;
  extraSeatPriceCents: number;
  /** Active locations right now, and what the plan includes/charges. */
  locations: number;
  includedLocations: number;
  /** null = this plan cannot buy extra locations (upgrade to Business). */
  extraLocationPriceCents: number | null;
  /**
   * The currency the amounts above are in — the deployment's, not the
   * company's. What a company charges its own customers and what it pays for
   * the app are separate currencies and need not match.
   */
  currencyCode: string;
  /** Whether this company already has a gateway subscription attached. */
  subscribed: boolean;
  /** What the live subscription is discounted by ("Founders 20% off"), or null. */
  discountLabel: string | null;
  /** A promo code stored at signup, waiting for the first checkout to spend it. */
  pendingPromoCode: string | null;
  /**
   * Whether the management screen can open for this company: a Customer
   * Portal session needs a Stripe customer to exist, and stays true after
   * cancellation, because invoices remain reachable.
   */
  portalAvailable: boolean;
  /**
   * What the back end can do — whether its hosted page manages the whole
   * subscription, whether plan changes are prorated, and where the card
   * fields live. The billing UI reads these rather than hard-coding the
   * gateway's behavior.
   */
  capabilities: SubscriptionCapabilities;
  /**
   * `gateway` when Stripe Tax owns sales tax on the subscription — the app
   * has no number to quote, but the UI must still say tax will be added so
   * the gateway's charge is never a larger number than the one the plan card
   * promised. `none` when no tax applies.
   */
  salesTax: { status: 'gateway' } | { status: 'none' };
  /**
   * How the subscription is paid, for the app's own billing screen — "Visa
   * ····4242". Null where the gateway's own hosted page shows it instead.
   */
  paymentMethodLabel: string | null;
}

// ---------------------------------------------------------------------------
// Customers and properties
// ---------------------------------------------------------------------------

/** Display-safe card-on-file details; the card itself lives with the provider. */
export interface CardOnFile {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  savedAt: string | null;
}

export interface Customer {
  defaultTeamId?: string | null;
  teamFlexible?: boolean;
  id: string;
  /** Which location this record belongs to. */
  locationId: string | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  displayName: string;
  email: string | null;
  phone: string | null;
  cardOnFile: CardOnFile | null;
  billingAddressLine1: string | null;
  billingAddressLine2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingCountryCode: string | null;
  internalNotes: string | null;
  /**
   * Days from an invoice's issue to its due date — Net 30 is 30. Null is the
   * residential answer: due on receipt. Snapshotted onto each invoice at
   * issue, so changing it later never restates a bill already sent.
   */
  invoiceTermsDays: number | null;
  /** The client's own purchase-order number, printed on their invoices. */
  invoicePoNumber: string | null;
  /**
   * Bill this customer once a month for the month's completed visits instead
   * of an invoice per visit. Off everywhere until switched on.
   */
  consolidatedBilling: boolean;
  /** Customer-approved promotional text outreach; separate from reminders. */
  smsMarketingEnabled: boolean;
  /** Customer-approved promotional email outreach. */
  emailMarketingEnabled: boolean;
  /**
   * Whether this customer wants to hear when an earlier time opens up.
   * Off until they say yes. The texts and emails are about open spots only.
   */
  wantsEarlierTimes: boolean;
  /**
   * Home (residential) or business (commercial). Defaults to residential.
   * Does not change tax treatment yet.
   */
  serviceSegment: 'residential' | 'commercial';
  /** Text-reminder consent. Only 'opted_in' permits a send. */
  smsConsentStatus: SmsConsentStatus;
  /**
   * The E.164 number the consent was given for. Consent does not follow a
   * customer to a new number, so this is what the send path compares against.
   */
  smsConsentPhone: string | null;
  smsConsentAt: string | null;
  /**
   * True when this opt-in was the checkbox on the quote request form or the
   * photo-quote upload. Optional so installed mobile apps, which only know
   * `smsConsentSource`, can ignore it. That field stays one of its original
   * values and is null for this consent.
   */
  smsConsentQuoteForm?: boolean;
  smsConsentSource: SmsConsentSource | null;
  /**
   * What Twilio's Lookup said about the number on file — 'mobile', 'landline',
   * 'voip' or 'unknown' — or null when nobody has asked about *this* number.
   *
   * A landline is worth showing before somebody types a note and presses send:
   * the segment is billed, the carrier drops the message, and the customer
   * never learns there was anything to read.
   */
  phoneLineType: PhoneLineTypeCode | null;
  /**
   * Whether `phone` is a number this app could text if consent were given —
   * false for a landline-shaped or unparseable number, which is why the UI can
   * explain that the opt-in box is unavailable rather than just disabling it.
   */
  smsReachable: boolean;
  /**
   * Whether a portal link is live for this customer. The link itself is only
   * shown at the moment it is minted — the database keeps the hash, like share links.
   */
  portalLinkActive: boolean;
  portalLinkCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ReengagementReason = 'inactive' | 'repeat_cancellations';
export type ReengagementStatus = 'open' | 'snoozed' | 'contacted' | 'dismissed';

export interface ReengagementChannelAvailability {
  allowed: boolean;
  reason: string | null;
}

export interface ReengagementCandidate {
  id: string;
  customerId: string;
  customerName: string;
  firstName: string | null;
  phone: string | null;
  email: string | null;
  reasons: ReengagementReason[];
  lastCompletedAt: string | null;
  daysSinceLastClean: number | null;
  cancellationCount: number;
  cancellationWindowDays: number;
  latestCancellationAt: string | null;
  status: ReengagementStatus;
  snoozedUntil: string | null;
  openedAt: string;
  channels: {
    call: ReengagementChannelAvailability;
    sms: ReengagementChannelAvailability;
    email: ReengagementChannelAvailability;
  };
  outreach: {
    smsBody: string;
    emailSubject: string;
    emailBody: string;
  };
}

export interface ReengagementSummary {
  open: number;
  snoozed: number;
  contacted: number;
  dismissed: number;
  total: number;
}

export interface ReengagementSettings {
  enabled: boolean;
  inactiveDays: number;
  cancellationCount: number;
  cancellationWindowDays: number;
  reminderCadenceDays: number;
  notificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  smsNotificationsEnabled: boolean;
  notificationsAvailable: boolean;
}

/**
 * One room saved on a home, in the shape the mobile app copies onto a quote.
 *
 * `name` is read first. `roomName` is the same text: a quote already stores
 * the room under that key, and the parser accepts it when `name` is blank.
 * Tasks are not listed here. `roomTemplateId` is the library row whose tasks
 * come along; a null id means the app matches the library by name.
 */
export interface PropertyRoom {
  name: string;
  roomName: string;
  quantity: number;
  roomTemplateId: string | null;
}

export interface Property {
  id: string;
  customerId: string;
  name: string | null;
  propertyType: PropertyType;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string;
  squareFeet: number | null;
  /** This property's own rate per square foot; null uses the company default. */
  areaRateCents: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  floors: number | null;
  /**
   * Where the property actually is, for the clock-in distance check. Filled
   * from address autocomplete when the address was picked from a suggestion,
   * editable never — corrected by re-picking the address. Null = distances
   * are simply not computed for this property.
   */
  latitude: number | null;
  longitude: number | null;
  occupancyStatus: OccupancyStatus | null;
  petsDescription: string | null;
  accessNotes: string | null;
  parkingNotes: string | null;
  internalNotes: string | null;
  /** The client's own reference for this building — "BR-114" (0087). */
  siteCode: string | null;
  /** The person at this building, distinct from the billing contact (0087). */
  siteContactName: string | null;
  siteContactTitle: string | null;
  siteContactEmail: string | null;
  siteContactPhone: string | null;
  /**
   * Rooms already saved on this home. An empty array means nothing is stored
   * (the column is null, or the snapshot did not parse). The mobile app reads
   * a missing field the same way.
   */
  rooms: PropertyRoom[];
  createdAt: string;
  updatedAt: string;
}

/**
 * One address the lookup service offered while somebody was typing.
 *
 * A suggestion, not a record: taking one fills the four address boxes and
 * nothing else, and every field stays editable afterwards. `id` is stable only
 * within the response it came in — it keys the option list, and is never
 * stored.
 */
export interface AddressSuggestion {
  id: string;
  /** The one-line form, which is what the option shows. */
  label: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  /**
   * Where the provider says this address is. The one part of a suggestion
   * that outlives the picking: the property form holds it while the address
   * stands as suggested, and saves it into `Property.latitude`/`longitude`
   * for the clock-in distance check. Null (or absent) when the provider did
   * not say, which costs nothing but the distance check.
   */
  latitude?: number | null;
  longitude?: number | null;
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface ServiceTemplate {
  id: string;
  name: string;
  customerDescription: string | null;
  /**
   * The same two texts in the company's other languages (see localized-text.ts).
   *
   * `name` above stays the company's own wording in its own language and is
   * what a customer document prints; these are what the app shows a member of
   * staff whose browser asks for a different one. Null on a task nobody has
   * translated — every reader sees `name`, which is what happened before this
   * field existed.
   */
  nameTranslations: LocalizedText | null;
  customerDescriptionTranslations: LocalizedText | null;
  /**
   * Which language this row's own base texts are written in.
   *
   * The editor needs it to file the base text into the map under the right
   * tag, and it cannot ask the company: `Company.language` is what the company
   * SENDS in and the owner changes it in Settings, while this row was written
   * once and stays written in whatever it was written in.
   */
  textLanguage: SupportedLanguage;
  category: string | null;
  /**
   * The grouping label in the company's other languages.
   *
   * Translated for the same reason the name is, and easy to miss: a task list
   * showing Spanish task names filed under English headings reads like a
   * half-finished translation, because it is one.
   */
  categoryTranslations: LocalizedText | null;
  defaultMinutes: number;
  minimumMinutes: number | null;
  maximumMinutes: number | null;
  isTaxable: boolean;
  isActive: boolean;
  showOnCustomerQuote: boolean;
  /**
   * How this task is usually estimated, and the rate to start from. 'time'
   * with a null rate is every task in the residential library: somebody
   * decided it takes eight minutes and typed eight.
   */
  measurementBasis: MeasurementBasis;
  productionRatePerHour: number | null;
  /**
   * The task's cost basis (0118): its labor's loaded hourly rate (null = the
   * company default), and the materials and travel one room of it costs per
   * visit. Optional so an older payload still parses. Staff only.
   */
  costLoadedHourlyCents?: number | null;
  costMaterialsCents?: number;
  costTravelCents?: number;
  displayOrder: number;
  /**
   * Stable category code for the picker (`libraryTaxonomy.task.*`). The free
   * text `category` above stays the company's own heading. Null until set.
   */
  categoryCode?: string | null;
  /** Residential/commercial, job type, and floor type. Null when untagged. */
  tags?: TemplateTags | null;
  isFavorite?: boolean;
  /** How many quote lines were added from this task. Drives "most used". */
  useCount?: number;
  /** Starter-library identity. Null on a task the company wrote themselves. */
  seedKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoomTemplateService {
  serviceTemplateId: string;
  defaultSelected: boolean;
  defaultMinutesOverride: number | null;
  /** This task's rate in this kind of area, when it differs from the task's. */
  defaultProductionRateOverride: number | null;
  /** Fixtures or square feet this task starts with inside this room type. */
  defaultMeasuredQuantity?: number | null;
  displayOrder: number;
}

export interface RoomTemplate {
  id: string;
  name: string;
  description: string | null;
  /** The same two texts in the company's other languages (see localized-text.ts). */
  nameTranslations: LocalizedText | null;
  descriptionTranslations: LocalizedText | null;
  /** Which language `name` and `description` are written in. */
  textLanguage: SupportedLanguage;
  iconName: string | null;
  /**
   * A price for the whole room instead of a price for its time. `null` means
   * this room type is priced from its task minutes, as every room type was
   * before flat pricing existed.
   */
  defaultFlatPriceCents: number | null;
  /**
   * Whether this kind of area is measured, and in what. 'time' — every room
   * type in the residential library — is the answer that asks for nothing: a
   * bedroom is a bedroom, and nobody measures it.
   */
  measurementBasis: MeasurementBasis;
  defaultFloorType: FloorType | null;
  displayOrder: number;
  isActive: boolean;
  /** Stable category code (`libraryTaxonomy.room.*`). Null until set. */
  categoryCode?: string | null;
  tags?: TemplateTags | null;
  isFavorite?: boolean;
  useCount?: number;
  seedKey?: string | null;
  /** How many of this room a quote starts with when the type is added. */
  defaultQuantity?: number;
  /** Starting floor area, when this kind of room is usually measured. */
  defaultSquareFeet?: number | null;
  services: RoomTemplateService[];
  createdAt: string;
  updatedAt: string;
}

/**
 * A group saved from a quote, with its rooms and tasks snapshotted so a later
 * edit to the room library does not rewrite the template.
 */
export interface GroupTemplateRoom {
  /** Room library row this snapshot was copied from, when it still exists. */
  roomTemplateId?: string | null;
  /**
   * Starter-library key, when this room was copied from one. The id is filled
   * in once the company's own room row exists; the key resolves it when the
   * snapshot was stored before that row did.
   */
  roomKey?: string | null;
  roomName: string;
  roomNameTranslations: LocalizedText | null;
  quantity: number;
  sizeCode: RoomSizeCode;
  conditionCode: RoomConditionCode;
  flatPriceCents: number | null;
  measuredSquareFeet: number | null;
  floorType: FloorType | null;
  customerNotes: string | null;
  /** Typed duration, when the estimator overrode the task sum before saving. */
  adjustedMinutes?: number | null;
  services: Array<{
    serviceTemplateId: string | null;
    /**
     * Starter-library key, when this task was copied from one. The id is filled
     * in once the company's own task row exists; the key is what resolves it
     * when the snapshot was stored before that row did.
     */
    serviceKey?: string | null;
    name: string;
    description: string | null;
    nameTranslations: LocalizedText | null;
    descriptionTranslations: LocalizedText | null;
    estimatedMinutes: number;
    isSelected: boolean;
    showOnCustomerQuote: boolean;
    isTaxable: boolean;
    frequency: ServiceFrequency;
    measurementBasis: MeasurementBasis;
    measuredQuantity: number | null;
    productionRatePerHour: number | null;
  }>;
}

export interface GroupTemplate {
  id: string;
  name: string;
  nameTranslations: LocalizedText | null;
  description: string | null;
  descriptionTranslations: LocalizedText | null;
  textLanguage: SupportedLanguage;
  notes: string | null;
  categoryCode: string | null;
  tags: TemplateTags | null;
  rooms: GroupTemplateRoom[];
  seedKey: string | null;
  isFavorite: boolean;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export interface QuoteRoomService {
  id: string;
  serviceTemplateId: string | null;
  name: string;
  description: string | null;
  /**
   * The two texts above in the company's other languages, snapshotted from the
   * task library when the line was added (see localized-text.ts) — so the crew sheet reads in
   * the cleaner's language while the quote reads in the customer's.
   *
   * Null once somebody types over the name: a hand-typed line is one string in
   * one language, and inventing the others would be worse than falling back.
   */
  nameTranslations: LocalizedText | null;
  descriptionTranslations: LocalizedText | null;
  estimatedMinutes: number;
  isCustom: boolean;
  isSelected: boolean;
  showOnCustomerQuote: boolean;
  /** Whether this task's share of labor is taxed (spec §9.10). */
  isTaxable: boolean;
  /**
   * How often this task is done — the commercial spec's vocabulary. Always
   * 'every_visit' on residential quotes, where the column simply never shows.
   */
  frequency: ServiceFrequency;
  /**
   * How this line's minutes were reached. Under 'area' or 'fixture'
   * `estimatedMinutes` above is DERIVED from the two fields below — it is
   * still the figure to display, print and schedule against, it is simply
   * not the figure anybody typed.
   */
  measurementBasis: MeasurementBasis;
  /**
   * Square feet under 'area', fixtures under 'fixture'. `null` under 'area'
   * means the task is measured over the area's own floor area.
   */
  measuredQuantity: number | null;
  /** Units per hour: square feet per hour, or fixtures per hour. */
  productionRatePerHour: number | null;
  /**
   * Done on the first visit only. Left out of the regular visit's price,
   * minutes and checklist while the quote has a first clean price.
   */
  firstVisitOnly?: boolean;
  customerNotes: string | null;
  internalNotes: string | null;
  displayOrder: number;
}

export interface QuoteRoom {
  id: string;
  roomTemplateId: string | null;
  roomName: string;
  /** The room's name in the company's other languages (see localized-text.ts). */
  roomNameTranslations: LocalizedText | null;
  quantity: number;
  sizeCode: RoomSizeCode;
  sizeMultiplierBasisPoints: number;
  conditionCode: RoomConditionCode;
  conditionMultiplierBasisPoints: number;
  baseMinutes: number;
  calculatedMinutes: number;
  adjustedMinutes: number | null;
  billableMinutes: number;
  isManuallyAdjusted: boolean;
  /**
   * This room's own price, copied from the room type when it was added and
   * editable from then on. `null` prices the room from its minutes.
   */
  flatPriceCents: number | null;
  /**
   * What the flat price comes to after quantity and the size and condition
   * multipliers. `null` on a room priced by time — its money is in the quote's
   * labor subtotal, not here.
   */
  chargeCents: number | null;
  /** The area's floor area, inherited by its measured tasks. */
  measuredSquareFeet: number | null;
  floorType: FloorType | null;
  customerNotes: string | null;
  internalNotes: string | null;
  displayOrder: number;
  /** The group this room sits in. Absent only on a draft that predates groups. */
  groupId?: string | null;
  services: QuoteRoomService[];
}

/**
 * A countable bundle of rooms on a quote: forty 3-bedroom apartments, the
 * common areas once, Building A. Time and price are the rooms' sum times
 * `quantity`. A quote always has at least one; the untouched default is what
 * a residential quote looks like when nobody has grouped it.
 */
export interface QuoteGroup {
  id: string;
  name: string;
  nameTranslations: LocalizedText | null;
  quantity: number;
  notes: string | null;
  isDefault: boolean;
  displayOrder: number;
}

export interface QuoteFee {
  id: string;
  name: string;
  description: string | null;
  amountCents: number;
  isTaxable: boolean;
  displayOrder: number;
}

export interface QuoteTotals {
  pricingBlocked?: boolean;
  estimatedMinutes: number;
  billableMinutes: number;
  hourlyRateCents: number;
  billingIncrementMinutes: number;
  laborSubtotalCents: number;
  /** Flat-priced rooms, after their multipliers. Zero when none are priced. */
  roomChargesCents: number;
  fixedFeesCents: number;
  discountType: DiscountType | null;
  discountValue: number;
  discountCents: number;
  /**
   * The accepted frequency tier's own reduction, separate from the
   * estimator's discount above so the two can be printed as the two different
   * things they are. Zero until a tier is accepted, and on every quote that
   * offers none.
   */
  frequencyDiscountCents: number;
  taxableSubtotalCents: number;
  taxRateBasisPoints: number;
  laborIsTaxable: boolean;
  taxCents: number;
  calculatedTotalCents: number;
  totalOverrideCents: number | null;
  totalOverrideReason: string | null;
  totalOverrideByUserId: string | null;
  totalOverrideAt: string | null;
  totalCents: number;
}

export interface QuoteVisitRequest {
  startsOn: string;
  startTimeLocal: string;
  timezone: string;
}

export interface QuoteSummary {
  /** An unresolved size decision has no publishable money estimate. */
  pricingBlocked?: boolean;
  /** Customer preference; a live schedule is what confirms the visit. */
  requestedVisit?: QuoteVisitRequest | null;
  id: string;
  /** Which location this record belongs to. */
  locationId: string | null;
  quoteNumber: string;
  status: QuoteStatus;
  /**
   * Snapshot taken at creation. Amounts are bare integer cents, so reading the
   * unit from the company's mutable setting would restate quotes already sent.
   */
  currencyCode: string;
  customerId: string;
  customerNameSnapshot: string;
  /**
   * On the summary rather than only the full quote because a list row is a
   * place work starts from: booking a visit straight off the quote list has to
   * carry the address that was quoted, and re-fetching every row to learn it
   * would be a request per line.
   */
  propertyId: string | null;
  propertyAddressSnapshot: string | null;
  quoteDate: string;
  expirationDate: string | null;
  /** Which kind of work this quote is. Fixed at creation, like its currency. */
  segment: QuoteSegment;
  /**
   * What `totalCents` buys: one visit, or a month of the specified service.
   * On the summary because a list that shows "$1,800" owes the reader
   * "/month" in the same glance.
   */
  billingBasis: BillingBasis;
  totalCents: number;
  /**
   * How the price was promised when it was sent from a photo quote: `range`
   * means the customer was shown a band and told the final price is set at
   * the visit, so `totalCents` is only the band's middle and a booking puts
   * the visits on the calendar without a price. Null for every other quote.
   */
  pricePromise?: 'firm' | 'range' | null;
  /**
   * The band a `range` quote was sent with, so a list can show it as the
   * range the customer saw rather than its middle. Null for every other quote.
   */
  priceRangeLowCents?: number | null;
  priceRangeHighCents?: number | null;
  /** How long the job takes, across every room, priced or hourly. */
  estimatedMinutes: number;
  /** The part of it charged by the hour. Not the job length — see above. */
  billableMinutes: number;
  /** Visits a week the monthly price covers. Null except under 'monthly'. */
  visitsPerWeek: number | null;
  /**
   * How long the ordinary visit takes — the schedule's number. Equal to
   * `estimatedMinutes` on per-visit quotes; on a monthly quote the latter
   * holds the month's labor, so a booking handoff that reads it would put a
   * whole month on one calendar slot. On the summary because the list is a
   * place booking starts from, same as `propertyId`.
   */
  routineVisitMinutes: number;
  /**
   * How many people the estimator plans to send. Divides the displayed and
   * scheduled time on site; never a pricing input — labor is billed by its
   * minutes whoever they are split across. 1 on every quote that predates
   * the field.
   */
  crewSize: number;
  /**
   * The wall-clock length of the ordinary visit:
   * `ceil(routineVisitMinutes / crewSize)`, derived server-side so no client
   * does its own division. This — not the labor figure — is what the booking
   * handoff puts on the calendar.
   */
  onSiteMinutes: number;
  version: number;
  revisionNumber: number;
  isExpired: boolean;
  updatedAt: string;
  createdAt: string;
  /**
   * On the summary too, not just the full quote: the list is where a download
   * link would live, and showing one for every row would send most clicks to a
   * 404 for quotes that were never generated.
   */
  pdfAvailable: boolean;
  /** Whether the customer page offers the frequency choice (migration 0110). */
  frequencyTiersEnabled: boolean;
  /**
   * How often this home would be cleaned. Chosen up front on a residential
   * quote (migration 0018). Null until somebody answers, and on every quote
   * that predates the question. Not the tier the customer accepted — that
   * is `acceptedFrequency`.
   */
  preferredFrequency?: SeriesFrequency | null;
  /**
   * The frequency tier the customer (or the office, recording a yes given on
   * the phone) chose. On the summary because a list that shows an accepted
   * quote's total owes the reader "/visit, weekly" in the same glance — and
   * because booking starts from the list, and the chosen tier is the series
   * the booking should offer first. Null = no choice recorded.
   */
  acceptedFrequency: SeriesFrequency | null;
  /**
   * The FIRST schedule this quote was booked as, if it has been booked at all
   * (migration 0117) — the link the quote offers back to the calendar.
   *
   * On the summary because it decides what the list row offers: a booked
   * quote gets a link to its schedule where an unbooked one gets the
   * "Schedule" button, and the two must not be one request apart or every row
   * would have to be re-read to know which it is.
   */
  scheduledSeriesId: string | null;
  /** When the first booking was made. Null exactly when `scheduledSeriesId` is. */
  scheduledAt: string | null;
  /**
   * How many schedules this quote has running.
   *
   * One schedule now holds every weekday the agreement visits, so a quote is
   * booked once this count is at least 1. Older quotes may still show more
   * than one, from when each weekday was its own series; ending those gives
   * the count back down.
   */
  scheduledSeriesCount: number;
  /**
   * How many series this quote may claim. Null means one — the current rule.
   * A number above one is a quote that was already partly booked as separate
   * weekday series, and still owes the rest (migration 0016).
   */
  seriesClaimCeiling?: number | null;
  /**
   * The quote this one was copied from by Book again (0037), the immediate
   * parent. Null for every other quote. The parent's number, and whether it
   * still exists, come with the job (`SingleJob.bookAgain`).
   */
  sourceQuoteId?: string | null;
  /** Set when Book again saved this copy as already approved (0037). */
  approvalCarryover?: QuoteApprovalCarryover | null;
}

/**
 * "Approved by carryover from Q-1042": the customer approved the quote this
 * copy was made from, and the copy costs the same or less with nothing
 * added, so it was saved as approved without asking again.
 */
export interface QuoteApprovalCarryover {
  fromQuoteNumber: string;
  /** When the approval it carries was given. For a chain, the first one's. */
  acceptedAt: string | null;
  acceptedByName: string | null;
}

export interface Quote extends QuoteSummary {
  bookingPending?: boolean;
  firstClean?: import('../first-clean-state.ts').FirstCleanBundle | null;
  acceptedFirstClean?: import('../first-clean-state.ts').AcceptedFirstClean | null;
  /** What the first visit adds; null prices it like the others. */
  firstCleanPrice?: import('../first-clean-price.ts').FirstCleanPrice | null;
  areaPricing?: import('../area-pricing.ts').QuoteAreaPricing;
  dust?: import('../dust-evidence.ts').QuoteDust;
  dustStatus?: ReturnType<typeof import('../dust-evidence.ts').dustStatus>;
  walkthrough?: import('../walkthrough-gate.ts').QuoteWalkthrough;
  walkthroughStatus?: ReturnType<typeof import('../walkthrough-gate.ts').walkthroughStatus>;
  /**
   * A `range` quote's width, plus and minus this percent of its total. Saving
   * an edit that moves the total moves the band by it, so the editor can show
   * the band the edit will give. Null for every other quote.
   */
  priceRangeWidthPct?: number | null;
  /**
   * The language this quote was WRITTEN in — its own snapshot, not the
   * reader's and not the company's current setting (0027).
   *
   * On the wire because the editor's Review step previews the customer's
   * document: its room and task names have to read the way the sent page and
   * the PDF will read them, which is this language, not the estimator's
   * (see localized-text.ts).
   */
  language: SupportedLanguage;
  customerEmailSnapshot: string | null;
  customerPhoneSnapshot: string | null;
  propertyTypeSnapshot: string | null;
  internalNotes: string | null;
  customerNotes: string | null;
  termsSnapshot: string | null;
  showTimeOnQuote: boolean;
  showHourlyRateOnQuote: boolean;
  sharingEnabled: boolean;
  /**
   * The raw share token is never stored (spec §15.1), so the customer link can
   * only be shown at the moment it is minted — see the publish response. This
   * flag tells the editor whether a live link exists to revoke or replace.
   */
  publicLinkActive: boolean;
  publicTokenCreatedAt: string | null;
  publicTokenExpiresAt: string | null;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  acceptedByName: string | null;
  declinedAt: string | null;
  declinedByName: string | null;
  /** The countable "why", when one was given. Free text stays in the audit trail and the customer's message. */
  declinedReason: QuoteDeclineReason | null;
  /** The acceptance signature, when the company required one and the customer gave it. */
  signature: { consentedAt: string; hasMark: boolean } | null;
  completedAt: string | null;
  pdfGeneratedAt: string | null;
  pdfIsStale: boolean;
  createdByUserId: string;
  updatedByUserId: string;
  /**
   * The agreement wrapped around a commercial quote, rendered under
   * "Agreement terms" and covered by the customer's acceptance. Null fields
   * mean what a missing clause means on paper. Always null-filled on
   * residential quotes.
   */
  contract: QuoteContractTerms;
  totals: QuoteTotals;
  /**
   * Optional so a draft saved before groups existed still typechecks. The
   * editor treats a missing list as one default group.
   */
  groups?: QuoteGroup[];
  rooms: QuoteRoom[];
  fees: QuoteFee[];
  /**
   * The offered frequency tiers, in the canonical order (one_time first).
   * Present whether or not `frequencyTiersEnabled` is on — switching the
   * offer off for one send keeps the configured discounts for the next.
   */
  frequencyTiers: QuoteFrequencyTier[];
}

/**
 * One offered frequency and its price (spec: frequency pricing tiers).
 * The discount fields are the estimator's inputs; the two cents figures are
 * the Worker's derivation through the pricing engine, snapshotted on the tier
 * row by the same write path that prices the quote.
 */
export interface QuoteFrequencyTier {
  frequency: SeriesFrequency;
  discountType: DiscountType | null;
  discountValue: number;
  /** What this tier takes off the quote as built. */
  frequencyDiscountCents: number;
  /** What a visit at this frequency costs — the tier's whole point. */
  totalCents: number;
}

export interface QuoteContractTerms {
  /** Length of the agreement, in months. Null = month-to-month. */
  termMonths: number | null;
  autoRenews: boolean;
  /** Annual increase in basis points — 300 is 3% on each anniversary. */
  escalationBasisPoints: number | null;
  /** Days of notice either side owes the other to end the agreement. */
  cancellationNoticeDays: number | null;
}

// ---------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------

/** One weekday of a schedule, as the API returns it. */
export interface SeriesDaySlot {
  /** 0 = Sunday … 6 = Saturday. */
  dayOfWeek: number;
  /** Monthly only: which weeks of the month. 5 means "last". */
  nthWeeks?: number[];
  /** Set only when this weekday starts at a different time from the schedule. */
  startTimeLocal?: string;
  /** Set only when this weekday runs for a different length. */
  durationMinutes?: number;
  /** Set only when this weekday is done by a different crew. Empty means unassigned. */
  assignedUserIds?: string[];
  /** Set only when this weekday has its own price, in the schedule's currency. */
  priceCents?: number;
  /** Set only when this weekday uses its own checklist template. */
  checklistTemplateId?: string;
}

export interface AppointmentSeries {
  teamId?: string | null;
  id: string;
  /** Which location this record belongs to. */
  locationId: string | null;
  customerId: string;
  /** Joined for display so lists don't need a lookup per row. */
  customerName: string;
  propertyId: string | null;
  quoteId: string | null;
  frequency: SeriesFrequency;
  /** First weekday, kept for clients that still read a single day. */
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  /** First nth, kept for clients that still read a single week-of-month. */
  nthWeek: number | null;
  /** Every N weeks. Null on a monthly or one-time schedule. */
  intervalWeeks: number | null;
  /** Every weekday this schedule visits, with any per-day time or crew. */
  daySlots: SeriesDaySlot[] | null;
  startTimeLocal: string;
  durationMinutes: number;
  timezone: string;
  startsOn: string;
  endsOn: string | null;
  horizonMode?: 'company_default' | 'until_date' | 'count';
  horizonUntil?: string | null;
  horizonCount?: number | null;
  status: SeriesStatus;
  internalNotes: string | null;
  visitPriceCents: number | null;
  /** A dated price change awaiting its date, or null when none is pending (migration 0078). */
  scheduledPriceChange: {
    /** Null only when the schedule has no price of its own and the change names weekday prices. */
    priceCents: number | null;
    effectiveOn: string;
    customerNotifiedAt: string | null;
    /** Each weekday's new price, on a schedule priced by weekday (0094). Null otherwise. */
    dayPrices: Array<{ dayOfWeek: number; priceCents: number }> | null;
  } | null;
  /**
   * The currency the price above was entered in. Snapshotted like `timezone`,
   * so a company that changes what it bills in does not silently redenominate
   * schedules already agreed. Null when there is no price.
   */
  priceCurrencyCode: string | null;
  billingMode: BillingMode;
  /** The lead assignee — `assignedUserIds[0]` — kept for older clients. */
  assignedUserId: string | null;
  /** The whole crew, lead first; empty = the company-wide calendar. */
  assignedUserIds: string[];
  /**
   * The agreement this schedule runs under, copied from the accepted quote at
   * booking and editable after — contracts get renegotiated. Null when this
   * schedule carries none, which is all residential work.
   */
  contract: SeriesContract | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeriesContract {
  /** The local date the term is counted from — anniversaries and renewal both. */
  startedOn: string;
  termMonths: number | null;
  autoRenews: boolean;
  escalationBasisPoints: number | null;
  cancellationNoticeDays: number | null;
  /** The local date the current term runs to, or null on a month-to-month. */
  renewsOn: string | null;
  /** Last anniversary an escalation price change has been scheduled for. */
  escalatedThrough: string | null;
}

export type LateCancelFeeStatus = 'pending' | 'charging' | 'charged' | 'waived';

export interface LateCancelFee {
  applies: boolean;
  /** The notice window this decision used, or the one that would apply. */
  noticeHours: number | null;
  amountCents: number | null;
  /** Snapshotted when the fee is recorded, so a later currency change cannot reprice it. */
  currencyCode: string | null;
  status: LateCancelFeeStatus | null;
  paymentId: string | null;
  invoiceId: string | null;
  waivedByUserId: string | null;
  decidedAt: string | null;
  /** Set only when a charge was just attempted and the card was not charged. */
  lastError?: string | null;
}

export interface Appointment {
  colors?: import('../schedule-colors.ts').VisitColors;
  keepTeamPrompt?: string | null;
  teamFlexible?: boolean;
  teamId?: string | null;
  visitType?: import('../checklists/stack.ts').ChecklistVisitType | null;
  checklistRevision?: number;
  id: string;
  /** Which location this record belongs to. */
  locationId: string | null;
  seriesId: string;
  customerId: string;
  propertyId: string | null;
  /** The date the rule produced; stays put when the visit is rescheduled. */
  occurrenceDate: string;
  scheduledDate: string;
  startTimeLocal: string;
  durationMinutes: number;
  timezone: string;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  confirmedAt: string | null;
  confirmedByName: string | null;
  changeRequestedAt: string | null;
  changeRequestMessage: string | null;
  /**
   * The time the customer picked for themselves on the public visit page,
   * when they picked one rather than typing a message (0012). Null on every
   * other change request. The office moves the visit to it in one click; it
   * is a proposal until they do, because only they can see the rest of the
   * day.
   */
  proposedScheduledDate: string | null;
  proposedStartTimeLocal: string | null;
  /**
   * The same instant in UTC, so the office's one-click move can tell whether
   * the hour is still ahead. The two local halves above cannot answer that on
   * their own — they are the customer's wall clock, and the reader's is
   * somewhere else.
   */
  proposedStartsAt: string | null;
  cancelledAt: string | null;
  cancellationInitiator: 'customer' | 'company' | 'system' | 'unknown' | null;
  cancellationReason: string | null;
  cancelledByUserId: string | null;
  /**
   * The late-cancellation fee on this visit.
   *
   * `applies` is true when the office is inside the notice window (the visit
   * is still open) or a fee was already recorded (the visit is canceled).
   * `status` is null until the visit is canceled inside the window. `lastError`
   * is only present on the response that tried to charge and could not.
   */
  lateCancelFee: LateCancelFee;
  completedAt: string | null;
  customerName: string;
  customerEmail: string | null;
  /**
   * The person at this building, when the site names one (0087). Snapshotted
   * with the visit, so re-staffing a building never rewrites who was told
   * about work already done. Null means notices went to `customerEmail`.
   */
  siteContactName: string | null;
  siteContactEmail: string | null;
  propertyAddress: string | null;
  internalNotes: string | null;
  priceCents: number | null;
  /**
   * The currency the price above was entered in — the snapshot automatic
   * billing reads to spot a price left behind by a currency change. Null when
   * there is no price.
   */
  priceCurrencyCode: string | null;
  paymentStatus: AppointmentPaymentStatus;
  /** The lead assignee — `assignedUserIds[0]` — kept for older clients. */
  assignedUserId: string | null;
  /** Everybody on this visit, lead first; empty = unassigned/company-wide. */
  assignedUserIds: string[];
  /** Minutes the visit actually took, recorded at completion. Null = untracked. */
  actualMinutes: number | null;
  /** Tasks marked done on this visit's checklist. 0 until the list is opened. */
  checklistCompletedCount: number;
  completionOverride?: { byUserId: string; reason: string; at: string; missingRequiredPhotos: number } | null;
  /** Tasks that count toward today's checklist. 0 until the list is opened. */
  checklistTotalCount: number;
  /** Who marked the visit completed. Null until then. */
  completedByUserId: string | null;
  /**
   * Who clocked in and out of this visit, oldest first. Served by the visit
   * detail read and the clock endpoints; absent on list rows, where loading
   * every visit's punches would be a query per line. The sum of closed
   * entries is what fills `actualMinutes` when the last person clocks out.
   */
  timeEntries?: TimeEntry[];
  /** Whether the CALLER is clocked in right now. Absent on list rows too. */
  myClockState?: 'in' | 'out';
  /**
   * The visit has no price yet and was booked from a quote sent as a price
   * range: completing it needs the final price. Detail read only.
   */
  finalPriceNeeded?: boolean;
  /**
   * The customer's confirmation page, e.g. /a/{id}.{signature}. Derived from a
   * server-side HMAC rather than stored, so it can appear in every reminder
   * email — and here, for staff to resend manually — without any minting step.
   * Null for read-only callers: the link is a credential that can change
   * appointment state, so it is only serialized for roles with schedule:write.
   */
  confirmPath: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One person's stretch on a visit's time clock. Distances are meters from the
 * property, computed server-side at the moment of the punch and stored — a
 * later correction to the property's coordinates must not rewrite what was
 * true when the button was pressed. Null distance = no fix was sent, or the
 * property has no coordinates; `onSite` is null for the same reasons.
 */
export interface TimeEntry {
  id: string;
  appointmentId: string;
  userId: string;
  /** Joined for display, so the visit panel needs no lookup per row. */
  userName: string;
  clockedInAt: string;
  clockedOutAt: string | null;
  /** Whole minutes between the punches; null while still clocked in. */
  minutes: number | null;
  inDistanceMeters: number | null;
  outDistanceMeters: number | null;
  /** Within ON_SITE_DISTANCE_METERS of the property at clock-in. */
  onSite: boolean | null;
  source: 'mobile' | 'web';
  /** Time nobody is paid for, like a trainee riding along (migration 0035). */
  unpaid: boolean;
  /** When the office last corrected this punch; null = exactly as punched. */
  editedAt: string | null;
}

/**
 * One busy stretch on the availability grid. Internal visits name what the
 * caller is allowed to see; external ones are an interval and nothing else —
 * the busy-only calendar promise, kept in the shape itself.
 */
export interface AvailabilityVisitBlock {
  /** Null for another location's visit — the row id is that branch's business. */
  appointmentId: string | null;
  seriesId: string | null;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  /**
   * Everybody on the visit; empty = unassigned, holding the hour for all.
   * On a cross-location block this is filtered to the people the caller
   * asked about — colleagues hidden by the roster boundary stay hidden.
   */
  assignedUserIds: string[];
  /** Null for another location's visit: the hour shows, the customer never. */
  customerName: string | null;
  otherLocation: boolean;
}

export interface AvailabilityExternalBlock {
  userId: string;
  startsAt: string;
  endsAt: string;
  /** busy | tentative | out_of_office | working_elsewhere */
  status: string;
}

/**
 * One quick-pick from GET /schedule/suggestions. Tolerant by design: the first
 * visit is guaranteed clear, later occurrences that clash are counted and
 * named rather than disqualifying — schedules shift, and moving the odd
 * future visit is normal.
 */
/**
 * Why a slot was offered — the one thing that made it better than the rest,
 * as a code the client puts into the reader's own words.
 */
export type SuggestionReason =
  /** One-off, hard against another job: the crew is driving out anyway. */
  | 'follows_job'
  /** One-off on a day the crew is already working, if not back to back. */
  | 'same_day'
  /** Repeating on a weekday the round already runs. */
  | 'existing_day'
  /** Fortnightly, on the emptier of the two alternating weeks. */
  | 'lighter_week'
  /** Monthly by weekday — "the second Tuesday", which every month has. */
  | 'monthly_weekday'
  /** Every occurrence of this rule lands clear. */
  | 'all_clear'
  /** Nothing else to say for it: the earliest slot that fits. */
  | 'soonest';

/**
 * Why the answer carries no picks at all — the branch the endpoint took, so
 * the form can say which it was instead of quietly showing nothing. An empty
 * list has several deliberate causes and they are not interchangeable: one is
 * the diary being full, the others are the search declining to guess.
 */
export type SuggestionsEmptyReason =
  /** Every candidate in the two-week fan-out is already taken. */
  | 'all_taken'
  /**
   * The scope has no working window to search at all — most often a crew
   * whose hours never overlap, which intersects to no days. Nothing was
   * scored against the diary, so this is emphatically not `all_taken`.
   */
  | 'no_working_hours'
  /**
   * Too many visits in the window to read them all, so nothing could be
   * scored honestly — "all clear" would have meant "clear among the ones we
   * happened to read".
   */
  | 'busy_truncated'
  /**
   * The crew's external-availability cache does not reach this far out, and
   * days past its edge were never cached — silence there is not "free".
   */
  | 'calendar_cache_short'
  /**
   * The whole search window is behind us, so nothing in it survives the
   * dispatch lead time. Only reachable by naming a past `from`.
   */
  | 'search_window_past';

export interface SlotSuggestion {
  /** Internal inherited-hours proposal requiring fresh booking confirmation. */
  runsPastClosing?: boolean;
  /** Calendar freshness prevents describing this pick as clear. */
  availabilityUncertain?: boolean;
  startsOn: string;
  startTimeLocal: string;
  /** The weekday a repeating rule starting here would repeat on. */
  dayOfWeek: number;
  /**
   * Which weekday of the month a monthly rule starting here would keep to
   * (1–4); null for every other frequency. Monthly suggestions are anchored
   * by weekday rather than by date, so the booking form has to be told to
   * store the rule that way — the date the pick lands on does not say it.
   */
  nthWeek: number | null;
  occurrencesChecked: number;
  occurrencesClashing: number;
  /** The first few clashing dates, so the office can see what would move. */
  clashingDates: string[];
  reason: SuggestionReason;
}

/** What GET /schedule/availability answers the booking grid with. */
export interface AvailabilityView {
  unavailable?: import('../types-availability.ts').AvailabilityBlock[];
  /** The zone the grid should draw its days in — the location's own clock. */
  timezone: string;
  /** Minutes of travel padding the company books around every visit. */
  bufferMinutes: number;
  /**
   * The working window for this scope — the crew's shared hours, or the
   * company's for the whole-company view. The grid shades everything outside
   * it; booking there stays possible, just visibly out of hours.
   */
  hours: { days: number[]; startLocal: string; endLocal: string };
  people: Array<{ userId: string; fullName: string }>;
  visits: AvailabilityVisitBlock[];
  external: AvailabilityExternalBlock[];
  /** People whose external-calendar picture is too old to trust. */
  staleUserIds: string[];
  truncated: boolean;
}

export interface Payment {
  id: string;
  customerId: string;
  appointmentId: string | null;
  /** The manual invoice this row settles, when it settles one (0103). */
  invoiceId: string | null;
  /**
   * The accepted quote this row was paid on, when it was paid on one
   * (0036_quote_payments.sql). At most one of `appointmentId`, `invoiceId` and
   * `quoteId` is set.
   */
  quoteId: string | null;
  /**
   * True when `quoteId` no longer names a quote that can be opened: it was
   * deleted, which a quote can be once all of its money was refunded. The row
   * stays in the ledger without a way to the quote. Set by a customer's
   * payment history, which links to quotes; absent elsewhere.
   */
  quoteDeleted?: boolean;
  type: PaymentType;
  method: PaymentMethodKind;
  status: PaymentStatus;
  amountCents: number;
  currencyCode: string;
  description: string | null;
  failureReason: string | null;
  provider: string;
  refundOfPaymentId: string | null;
  /** Part of `amountCents`: the card-processing fee added at charge time. */
  cardFeeCents: number;
  /** The rate that produced it, so an old row still explains itself. */
  cardFeeBasisPoints: number;
  /** Part of `amountCents`: the tip the customer added. Zero on refunds. */
  tipCents: number;
  /**
   * Whether the provider account this payment was taken through is still
   * connected. False means a refund would be refused however the arithmetic
   * looks, so the button that offers one should not. Always true for money
   * with no provider behind it.
   */
  providerAccountConnected: boolean;
  /** For `manual` rows: which of the company's payment methods was used. */
  manualMethodId: string | null;
  /** Snapshot of the method's name when the payment was recorded. */
  manualMethodName: string | null;
  /** When the money actually changed hands (manual rows may be backdated). */
  receivedAt: string | null;
  /**
   * Money that arrived in another currency than the one of what it pays
   * (0115): what was handed over, and the rate (as typed, "1.0834") that
   * turned it into `amountCents` in `currencyCode`. Null for every other row.
   */
  received?: { currencyCode: string; amountCents: number; exchangeRate: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** One currency's rate against the company's own (GET /exchange-rates). */
export interface ExchangeRate {
  currencyCode: string;
  /** How many of the company currency one unit buys, as a decimal string; null when none is kept. */
  rate: string | null;
  source: 'manual' | 'automatic' | null;
  /** The date the rate is for. */
  asOf: string | null;
  updatedAt: string | null;
}

export interface ExchangeRatesResponse {
  /** The company's own currency: every rate is against it, and reports convert into it. */
  baseCurrencyCode: string;
  /** Every other currency the company deals in, with its rate when it has one. */
  rates: ExchangeRate[];
}

/**
 * One quote's ledger, from `GET /payments/quotes/:quoteId`
 * (0036_quote_payments.sql).
 */
export interface QuotePaymentLedger {
  items: Payment[];
  /** The quote's total, in `currencyCode`. */
  totalCents: number;
  currencyCode: string;
  /**
   * Paid, Partly paid or Unpaid, with what is paid and what is left. Null
   * where the quote has no such figure: not accepted, recurring or monthly,
   * or nothing to pay. Its payments are listed either way.
   */
  summary: QuotePaymentSummary | null;
  /** Whether the quote sells one visit rather than a schedule. */
  oneTime: boolean;
  /** Whether a new payment may be recorded on it now (accepted or completed). */
  takesPayments: boolean;
  /**
   * Whether it has a job booked or done (`single_job`): the job takes the
   * payment from then on, so the quote takes no new one.
   */
  jobBooked: boolean;
  /**
   * Whether a canceled booking of it still holds a payment: the quote takes
   * no new one until that is refunded, on the job, so the customer is not
   * asked for the money twice. Absent from servers before it was added.
   */
  jobHoldsPayment?: boolean;
}

/**
 * Where the money on one job stands. Partly paid only when something was
 * recorded on the quote before the job was booked: a booked visit itself
 * takes one payment, for the whole balance.
 */
export type SingleJobPaymentStatus = QuotePaymentStatus;

/** Why a Book again copy needs the customer's approval before it is booked. */
export type BookAgainApprovalReason =
  'price_up' | 'line_added' | 'approval_old' | 'currency_changed' | 'customer_changed' | 'source_not_approved';

/** The one visit a job was booked as: an ordinary appointment. */
export interface SingleJobVisit {
  appointmentId: string;
  seriesId: string;
  status: AppointmentStatus;
  scheduledDate: string;
  startTimeLocal: string;
  durationMinutes: number;
  startsAt: string;
  endsAt: string;
  timezone: string;
  completedAt: string | null;
  cancelledAt: string | null;
  /** What the visit costs, in `SingleJob.money.currencyCode`. */
  priceCents: number | null;
  paymentStatus: AppointmentPaymentStatus;
}

/**
 * One approved one-time quote as a job (`GET /quotes/:quoteId/job`): book
 * it, do the work, get paid, and Book again. On every plan (`single_job`);
 * the Free plan is where the web shows it.
 */
export interface SingleJob {
  quoteId: string;
  /** Whether the quote sells one visit. Recurring and monthly quotes are not jobs. */
  oneTime: boolean;
  /** Accepted or completed, one-time, with no visit booked: Book it is offered. */
  canBook: boolean;
  /** The booked visit, or the last one if it was canceled. Null before the first booking. */
  visit: SingleJobVisit | null;
  money: {
    currencyCode: string;
    /** The visit's price once booked, the quote's total before. */
    totalCents: number;
    /** Recorded on the quote before the job was booked. Counts toward the balance. */
    paidOnQuoteCents: number;
    paidOnVisitCents: number;
    /** What the payment link, or a payment recorded on the visit, has to be for. */
    balanceCents: number;
    status: SingleJobPaymentStatus;
  };
  /** A payment link that was created and not yet paid, canceled or replaced. */
  openLink: { paymentId: string; amountCents: number; createdAt: string } | null;
  /** The payments on the visit, refunds included, newest first. */
  visitPayments: Payment[];
  /** The billing customer's address: where a payment link is emailed. */
  customerEmail: string | null;
  /**
   * Where the booking, change and cancellation emails go: the visit's site
   * contact when it has one, otherwise the customer. Null when neither has an
   * address, so no email goes out.
   */
  noticeEmail: string | null;
  /** Whether a card account is connected, so a payment link can be made. */
  cardPaymentsReady: boolean;
  /**
   * Book again is offered: an approved quote that is itself one-time, booked
   * or done. Read from the quote, as the copy is, not from the visit's series.
   */
  canBookAgain: boolean;
  /** Present when this quote is a Book again copy. */
  bookAgain: {
    /** The parent, while it still exists. */
    source: { id: string; quoteNumber: string } | null;
    /**
     * Null when this copy can be booked as it stands: approved by the
     * customer, or able to carry its parent's approval. Otherwise why it has
     * to go to the customer first.
     */
    needsApproval: BookAgainApprovalReason | null;
    /** Book again copies for this customer in the last 90 days, this one included. */
    copiesInLast90Days: number;
  } | null;
}

/**
 * Who a job's booking, change or cancellation email went to. `emailedTo` when
 * it went out, `notSentTo` when the email provider refused or suppressed it,
 * both null when there was no address to send to.
 */
export interface SingleJobCustomerNotice {
  emailedTo: string | null;
  notSentTo: string | null;
}

/** The job after booking, changing or canceling it, with what its email did. */
export interface SingleJobActionResult extends SingleJob {
  customerNotice: SingleJobCustomerNotice | null;
}

/** One row of the Booked list: a quote with a visit still to happen. */
export interface BookedJob {
  quoteId: string;
  quoteNumber: string;
  customerId: string;
  customerName: string;
  appointmentId: string;
  scheduledDate: string;
  startTimeLocal: string;
  startsAt: string;
  timezone: string;
  /**
   * What the job costs, as its card says: the quote's total until money moves
   * on the visit, then the visit's price.
   */
  amountCents: number | null;
  currencyCode: string;
  /**
   * Paid, Partly paid or Unpaid, measured as the job card measures it: what
   * was paid on the quote and on the visit, against `amountCents`.
   */
  paymentStatus: SingleJobPaymentStatus;
  /** The quote this job was booked again from, for the Repeat tag. */
  sourceQuote: { id: string; quoteNumber: string } | null;
}

/** One accepted quote with money still owed on it. */
export interface OwedQuote {
  quoteId: string;
  quoteNumber: string;
  customerId: string;
  customerName: string | null;
  /** The quote's own location: an all-locations list opens it there. */
  locationId: string | null;
  totalCents: number;
  /** What the payments recorded on the quote have settled. */
  paidCents: number;
  balanceCents: number;
  status: QuotePaymentStatus;
  acceptedAt: string | null;
}

/**
 * What is still owed on accepted one-time quotes, from `GET /payments/owed`:
 * each quote's total less what was paid on it. Booked quotes are left to their
 * visits, and recurring or monthly quotes are paid visit by visit, so neither
 * counts here.
 */
export interface OwedOnQuotes {
  currencyCode: string;
  /** Every quote that owes something, summed — not just the ones listed. */
  totalCents: number;
  count: number;
  /** Oldest acceptance first, bounded. */
  quotes: OwedQuote[];
}

/**
 * One line of a manual invoice (migration 0103). `unitCents` and
 * `amountCents` are signed: a negative line is a credit.
 */
export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitCents: number;
  amountCents: number;
}

/**
 * One per scheduled visit (migration 0032), or one the office wrote by hand
 * out of line items (`kind: 'manual'`, migration 0103). A visit invoice
 * carries no amount of its own — `priceCents`/`currencyCode` are the visit's,
 * read through the join the API always returns this with — and `status`
 * mirrors the visit's own `paymentStatus` rollup, plus `void` for a canceled
 * visit. A manual invoice has no visit: its money is `totalCents`, its status
 * rolls up from the payments carrying its id, and every visit-shaped field
 * here is null.
 */
export interface Invoice {
  id: string;
  customerId: string;
  /** 'visit' or 'manual'. Monthly invoices have their own shape. */
  kind: 'visit' | 'manual';
  /** Null exactly when `kind` is 'manual'. */
  appointmentId: string | null;
  invoiceNumber: string;
  status: InvoiceStatus;
  /** The visit's own price; null when the visit has none set yet, and on manual invoices. */
  priceCents: number | null;
  /**
   * A manual invoice's own figure, SIGNED — negative is a credit memo, money
   * the company owes the customer back. Null on visit invoices.
   */
  totalCents: number | null;
  currencyCode: string | null;
  /** The visit's own scheduled date (YYYY-MM-DD). Null on manual invoices. */
  scheduledDate: string | null;
  /**
   * The visit's start instant. On the list row, not just the detail, because
   * the office decides from the list: an invoice exists up to 84 days before
   * its cleaning does, and "has this work happened yet" is what says whether
   * the row may offer to send the customer their invoice. Null on manual
   * invoices, which have no work to wait for.
   */
  startsAt: string | null;
  appointmentStatus: AppointmentStatus | null;
  customerName: string;
  propertyAddress: string | null;
  /**
   * Whether the customer has a card this app could charge right now.
   *
   * On the invoice rather than fetched per row: the list offers a "Charge
   * card" action, and the only alternative to carrying the answer here is
   * offering it to everybody and letting the endpoint refuse.
   */
  hasCardOnFile: boolean;
  issuedAt: string;
  /**
   * The customer's payment terms at issue, and the local date the money is
   * due. Both null on invoices issued before net terms existed and for
   * customers without terms — due on receipt, aged from `issuedAt`.
   */
  termsDays: number | null;
  dueOn: string | null;
  /** The customer's PO number as it stood at issue. */
  poNumber: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A consolidated monthly invoice: one document covering a calendar month of
 * a customer's completed visits (migration 0082). Its total is snapshotted at
 * generation; payment still settles visit by visit, so `paidVisitCount` /
 * `visitCount` is how far along the month is.
 */
export interface MonthlyInvoice {
  id: string;
  customerId: string;
  customerName: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  currencyCode: string;
  termsDays: number | null;
  dueOn: string | null;
  poNumber: string | null;
  issuedAt: string;
  visitCount: number;
  paidVisitCount: number;
  voidedAt: string | null;
}

/**
 * A monthly invoice with what it covered: the month's visits, and the extra
 * work the client approved that rode out on the same document (0088).
 */
export interface MonthlyInvoiceDetail extends MonthlyInvoice {
  visits: Array<{
    appointmentId: string;
    scheduledDate: string;
    priceCents: number | null;
    currencyCode: string;
    paymentStatus: string;
    status: string;
    propertyAddress: string | null;
  }>;
  changeOrders: Array<{
    id: string;
    number: string;
    title: string;
    amountCents: number;
    currencyCode: string;
    approvedAt: string | null;
    approvedByName: string | null;
    serviceDate: string | null;
  }>;
  /**
   * The supplies this document charged for (0090). Listed because the total
   * already included them, and an invoice a client's accounts-payable
   * department cannot make add up is not doing its one job.
   */
  supplies: Array<{
    id: string;
    itemName: string;
    siteName: string | null;
    quantity: number;
    unitPriceCents: number;
    amountCents: number;
    currencyCode: string;
    deliveredAt: string | null;
  }>;
}

/**
 * One invoice with everything its detail panel and its PDF put on the page:
 * the visit's own timing, the customer's contact details, and — the reason the
 * endpoint exists at all — the ledger behind it. The money against an invoice
 * lives on the visit's payments, so an invoice that showed only a status badge
 * could not answer "what has been paid, and when".
 */
export interface InvoiceDetail extends Invoice {
  /** The local wall clock the visit was booked for; the instant is on `Invoice`. Null on manual invoices. */
  startTimeLocal: string | null;
  durationMinutes: number | null;
  /** The zone `startTimeLocal` is stated in — the visit's own, not the reader's. Null on manual invoices. */
  timezone: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  /** A manual invoice's lines, in order. Empty on visit invoices. */
  lineItems: InvoiceLineItem[];
  /**
   * A visit invoice's lines when the visit has add-ons or price changes: the
   * visit itself (`label` null) and then each line by name, adding up to the
   * visit's price. Empty when the visit is one plain line, and on manual
   * invoices.
   */
  visitLines: import('../visit-pricing.ts').VisitInvoiceLine[];
  /**
   * The ledger this invoice settles from, oldest first — the visit's rows for
   * a visit invoice, the rows carrying this invoice's id for a manual one.
   */
  payments: Payment[];
  totals: {
    /** The visit's currency, or the company's when the visit has no price yet. */
    currencyCode: string;
    paidCents: number;
    refundedCents: number;
    balanceCents: number;
    /**
     * A credit memo's unreturned remainder — how much the company still owes
     * back. Zero everywhere else.
     */
    creditRemainingCents: number;
  };
}

/**
 * Why one invoice in a bulk send was not sent. Every one a fact about the
 * invoice rather than about the request, which is what makes them safe to
 * report per row and to translate into a sentence beside that row.
 */
export type InvoiceSendRefusal =
  | 'cancelled'
  | 'not_started'
  | 'unpriced'
  | 'location_archived'
  | 'settled'
  | 'payment_in_flight'
  | 'no_address'
  | 'price_changed'
  | 'credit';

/**
 * What became of each invoice the office ticked.
 *
 * `undelivered` is a send that happened and reached nobody — a suppressed or
 * refused address — kept apart from both `sent` and `skipped` because it is
 * neither: the message left, and the customer still has not got it.
 */
export interface InvoiceBatchSendResult {
  results: Array<{
    invoiceId: string;
    /** Null when the id named no invoice this session can see. */
    invoiceNumber: string | null;
    status: 'sent' | 'undelivered' | 'skipped' | 'not_found';
    refusal: InvoiceSendRefusal | null;
    emailedTo: string | null;
  }>;
  sent: number;
  undelivered: number;
  /** Everything that did not go: refused rows and ids that named nothing. */
  skipped: number;
}

/**
 * One entry in the company's own list of ways it accepts money — the ways a
 * `manual` ledger row can be recorded. Seeded with defaults on registration
 * and editable in Settings, with or without a payment provider connected.
 */
export interface CompanyPaymentMethod {
  id: string;
  name: string;
  /**
   * The label in the company's other languages.
   *
   * A seeded, translated, owner-maintained list read from a picker by whoever
   * is recording the money — the same shape as the room and task library, and
   * localized for the same reason. What the customer sees is the snapshot on
   * the payment, which is a record of what was entered.
   */
  nameTranslations: LocalizedText | null;
  /** Which language `name` is written in; see `ServiceTemplate.textLanguage`. */
  textLanguage: SupportedLanguage;
  family: PaymentMethodFamily;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * A payment configuration row as the UI sees it — the company's, or a single
 * location's own account (`scope: 'location'`, fetched with `?locationId=`).
 * The credentials themselves are write-only; what comes back identifies them
 * (mode, last four) and says where the provider should send webhooks —
 * `webhookUrl` is the company endpoint for company settings and the
 * per-location endpoint for a location's own account.
 */
export interface PaymentSettings {
  /** Whose stored row this is. A location with no row still answers 'location', unconfigured. */
  scope: 'company' | 'location';
  provider: string;
  /**
   * The processor the stored row actually names, when this build has no
   * adapter for it — `dejavoo`, `valor` and the rest the baseline's CHECK
   * still permits. `provider` above is always one this build can drive, so
   * without this a dead account is indistinguishable from a Stripe one:
   * Settings would show it connected while `providerOf` returns null and
   * nothing can be charged or refunded through it.
   */
  unsupportedProvider: string | null;
  configured: boolean;
  /**
   * The Stripe account linked through Connect OAuth (`acct_…`), or null for
   * a pasted-key connection. Set, the key fields below are all null: there
   * is no per-company secret to show the last four of.
   */
  stripeConnectAccountId: string | null;
  enabled: boolean;
  keyMode: 'test' | 'live' | null;
  secretKeyLast4: string | null;
  /**
   * The provider's non-secret extra credentials — merchant id, client id,
   * public key — echoed back so Settings can display what is connected.
   * Secret fields are deliberately absent: they name the account, they do
   * not open it.
   */
  credentials: Record<string, string>;
  /** Every extra field that has a stored value, secret ones included. */
  credentialsConfigured: string[];
  webhookConfigured: boolean;
  webhookUrl: string;
  /** Adds a percentage when a customer pays online by card. Off by default. */
  cardFeeEnabled: boolean;
  /** Basis points, so 2.9% is 290. */
  cardFeeBasisPoints: number;
  /**
   * Charge the stored card the moment a cleaning is marked completed, for
   * every customer who has one. Off by default; customers with no card are
   * untouched by it and still get a payment link from the office.
   */
  autoChargeCardOnFile: boolean;
  updatedAt: string | null;
}

/**
 * A company's own Twilio configuration as the UI sees it.
 *
 * The auth token is write-only; what comes back identifies it (last four) and
 * says where Twilio should send inbound messages. `activeSource` is the field
 * that answers the question an owner actually has — which account this
 * company's texts are going out on — because a deployment that has Twilio
 * configured keeps sending on its own credentials no matter what is stored
 * here.
 */
/**
 * Why the texting webhooks on a company's own Twilio number are not set up.
 *
 *  - `twilio_unreachable`: Twilio could not be reached; trying again fixes it.
 *  - `not_on_account`: the sending number is not on the connected account.
 *  - `service_missing`: the Messaging Service entered is not on the account.
 *  - `service_not_ours`: the Messaging Service entered is the owner's own (not
 *    one BlastoClean created), and BlastoClean has not been agreed to use it
 *    yet. With `needs_confirmation`, `takeover` (option `use_service`) says
 *    what using it changes: its incoming-message and status-callback hosts
 *    now, and whether the number is added to it. Once agreed, that service is
 *    adopted: set up again without asking, never deleted.
 *  - `in_other_service`: the number is in a Messaging Service BlastoClean did
 *    not create (and, for an entered service, not that one). Take it out of
 *    that service in Twilio, then set up again. BlastoClean never takes a
 *    number out of a service that is not its own.
 *  - `did_not_stick`: Twilio accepted the change, but reading it back showed
 *    something else.
 *  - `setup_failed`: Twilio refused the change (`twilioDetail` has its words).
 *  - `no_public_url`: this installation has no public address for Twilio.
 *  - `secrets_unreadable`: the stored Auth Token or API key cannot be read.
 *  - `number_url_elsewhere` / `number_app_elsewhere` / `fallback_elsewhere`:
 *    the number's own SMS URL, SMS TwiML App, or SMS fallback URL points at
 *    another app; waiting for the owner to agree to replace it (`takeover`
 *    says what).
 */
export type TwilioSetupReason =
  | 'twilio_unreachable'
  | 'not_on_account'
  | 'service_missing'
  | 'service_not_ours'
  | 'in_other_service'
  | 'did_not_stick'
  | 'setup_failed'
  | 'no_public_url'
  | 'secrets_unreadable'
  | 'number_url_elsewhere'
  | 'number_app_elsewhere'
  | 'fallback_elsewhere';

/**
 * What an owner can agree to:
 *
 *  - `take_over`: replacing the address another app set on this one number
 *    (its SMS URL or TwiML App, and its SMS fallback URL) and putting the
 *    number in BlastoClean's Messaging Service;
 *  - `use_service`: using the owner's own Messaging Service entered in the
 *    form (one an A2P campaign may be approved on): pointing its incoming-
 *    message URL and status callback at BlastoClean, letting it answer
 *    STOP/HELP, clearing its fallback URL, and adding the number to it.
 */
export type TwilioTakeoverOption = 'take_over' | 'use_service';

export interface TwilioTakeover {
  /**
   * The host the number's texts go to now (never the full URL), or null when
   * there is none. For `use_service`: the host of the service's incoming-message URL.
   */
  previousHost: string | null;
  /** The host of the number's (or, for `use_service`, the service's) fallback URL, or null when there is none. */
  previousFallbackHost: string | null;
  /** `use_service` only: the owner's Messaging Service it is about. */
  serviceSid?: string;
  /** `use_service` only: the host of the service's status callback now (never the full URL), or null. */
  previousStatusHost?: string | null;
  /** `use_service` only: whether the number is added to the service (it is not in it yet). */
  addsNumber?: boolean;
  options: TwilioTakeoverOption[];
  /** Echoed back with the owner's choice, so agreeing never takes over something they were not shown. */
  target: string;
}

export interface TwilioSetupView {
  /** Null until setup has run for these settings. */
  status: 'configured' | 'needs_confirmation' | 'needs_attention' | null;
  reason: TwilioSetupReason | null;
  /** Twilio's own words for a refusal, when it gave some. */
  twilioDetail: string | null;
  /** The number whose replies were set up (E.164). */
  number: string | null;
  /** `number`: its own SMS URL takes its replies; `service`: its Messaging Service's URL does. */
  route: 'number' | 'service' | null;
  messagingServiceSid: string | null;
  /**
   * True when BlastoClean created that Messaging Service on the owner's
   * account. False with `route` 'service' and status 'configured': the
   * owner's own service, which they agreed BlastoClean may use.
   */
  messagingServiceOwned: boolean;
  takeover: TwilioTakeover | null;
  checkedAt: string | null;
  /**
   * The call side, for a company that has set up calls on its own number (the
   * Calls card says more). Null when calls are not in use.
   */
  calls: {
    status: 'configured' | 'needs_attention' | 'not_set_up';
    number: string | null;
    reason: CallLineProblemReason | null;
  } | null;
}

/**
 * The texting registration (A2P 10DLC) of a company's own Twilio account, for
 * Settings → Texting: the brand the owner registers in the Twilio Console with
 * the guide, and the campaign BlastoClean files on it (services/own-a2p.ts).
 */
export interface OwnA2pView {
  /**
   * Why BlastoClean cannot file here yet, or null when it can: no own account
   * connected, or its number not set up in BlastoClean's Messaging Service.
   */
  unavailableReason: 'no_account' | 'no_service' | 'not_10dlc' | null;
  /** How the account is connected: the guide differs (a Connect subaccount needs the brand shared with it). */
  connection: 'credentials' | 'connect' | null;
  /** The account's brands, as Twilio lists them. Null when Twilio could not be asked. */
  brands: Array<{ sid: string; status: string; brandType: string | null; failureReason: string | null }> | null;
  /** The campaign BlastoClean filed (or found on its Messaging Service), or null. */
  registration: {
    status: 'pending' | 'approved' | 'failed';
    brandSid: string;
    campaignSid: string | null;
    useCase: string;
    /** The carriers' rejection codes, each worded with its fix on the settings card. */
    failureCodes: number[];
    failureDetail: string | null;
    content: OwnA2pContent;
    submittedAt: string;
    checkedAt: string | null;
  } | null;
  /**
   * Whether the account holds a campaign the card does not show: one the owner
   * filed in the Console on BlastoClean's Messaging Service and has not checked
   * for, or one filed before the number became one BlastoClean cannot file for.
   * It stays, and is billed, after a disconnect. Null when Twilio could not say.
   */
  untrackedCampaign: boolean | null;
  /** What BlastoClean would file: the registration's own content when there is one, else freshly built. */
  draft: OwnA2pContent;
  /** The business details to type into the Twilio Console, from the company's profile. */
  business: {
    legalName: string;
    website: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
}

/** The editable campaign fields (`ownA2pCampaignContentSchema`). */
export interface OwnA2pContent {
  description: string;
  messageFlow: string;
  samples: string[];
  optInMessage: string;
  privacyPolicyUrl: string;
  termsAndConditionsUrl: string;
}

export interface SmsSettings {
  /**
   * Whether the deployment's shared sender is what sends. Always false now:
   * company traffic no longer uses a shared sender. Kept so older clients keep
   * parsing the response.
   */
  applicationConfigured: boolean;
  /** Whether this company has stored an account that can send. */
  configured: boolean;
  /**
   * How the stored account was connected: pasted credentials, or BlastoClean's
   * Twilio Connect App (no Auth Token stored; Twilio bills the owner's
   * account all the same). Null when none is stored.
   */
  connection: 'credentials' | 'connect' | null;
  /**
   * Whether those stored credentials can actually be used.
   *
   * False with `configured` true means the row is complete but unusable — in
   * practice an auth token encrypted under a key this installation no longer
   * has. It is deliberately not folded into `configured`: the owner did save
   * credentials, and telling them they did not would send them looking for the
   * wrong problem.
   */
  usable: boolean;
  /** Whether the company's own account is switched on. */
  enabled: boolean;
  accountSid: string | null;
  authTokenLast4: string | null;
  /** The API key SID (SK…), when the company connected one. An identifier, not a secret. */
  apiKeySid: string | null;
  /** The key secret's last four characters, or null. The secret itself is never returned. */
  apiKeySecretLast4: string | null;
  /**
   * Whether replies and STOPs to this company's own number can be verified.
   * False when no Auth Token is stored: Twilio signs webhooks with the Auth
   * Token only, and an API key cannot stand in for it.
   */
  webhooksVerifiable: boolean;
  messagingServiceSid: string | null;
  fromNumber: string | null;
  /**
   * Which credentials this company's messages actually use, if any.
   * `application` is no longer produced — it names the retired shared sender —
   * and remains so older clients and old sms_log rows still parse.
   */
  activeSource: 'platform_managed' | 'application' | 'company' | null;
  /**
   * Where replies to the company's own Twilio number go. Set up on the number
   * or its Messaging Service automatically (see `twilioSetup`); shown only for
   * reference.
   */
  inboundWebhookUrl: string;
  /** Where Twilio reports delivery of the company's own messages. Shown for reference. */
  statusCallbackUrl: string;
  /**
   * How setting up the own account's Twilio webhooks went, or null when no
   * account is connected.
   */
  twilioSetup: TwilioSetupView | null;
  updatedAt: string | null;
  /**
   * The sending number the operator issued this company, if it has asked for
   * one. Null on a deployment that does not offer them, or before the owner
   * requests one.
   *
   * Separate from every other field here: those describe credentials the
   * COMPANY supplied, and this describes a sender the OPERATOR issued. A
   * company can have both — connecting your own Twilio account does not hand
   * back the managed number — and `activeSource` is what says which is
   * actually sending.
   */
  managedNumber: ManagedSmsNumber | null;
  /**
   * Whether this installation can issue dedicated numbers at all.
   *
   * Asked of the operator's ACCOUNT credentials, not of the currently resolved
   * sending source — and the distinction is the whole reason the field exists.
   * `applicationConfigured` is false on an installation that has account
   * credentials but no shared sender, and `activeSource` is null until a
   * managed number already exists, so deriving visibility from either made the
   * account-only deployment mode impossible to bootstrap: the panel that
   * requests the first number was hidden until a number existed.
   *
   * Also false while FEATURE_MANAGED_SMS is off (the default); `managedNumber`
   * still describes a number issued before that.
   */
  managedNumbersAvailable: boolean;
  /**
   * Whether THIS company could actually be issued one right now.
   *
   * Distinct from `managedNumbersAvailable`, which is about the installation.
   * This mirrors the server's own request gate — paid subscription, and a
   * country whose registration flow is implemented — so the page can decline to
   * offer a control that is guaranteed to be refused. A trial passes
   * `requireFeature('sms')` because a trial carries every feature, which is
   * exactly why the UI cannot work this out from the plan alone.
   */
  managedNumberEligible: boolean;
  /** Why not, when `managedNumberEligible` is false. Shown to the owner. */
  managedNumberIneligibleReason: string | null;
}

/**
 * A sending number on the operator's account that belongs to one tenant.
 *
 * The tenant never sees a credential: no SID to paste, no auth token, no
 * webhook to register. What they see is a phone number, and how far along it
 * is.
 */
export interface ManagedSmsNumber {
  id: string;
  /**
   * Where provisioning has got to. Only `active` sends.
   *
   *   pending       queued, nothing bought yet
   *   provisioning  buying the number and building the sender
   *   registering   bought, waiting on carrier brand/campaign approval — the
   *                 slow step, and why this is asynchronous at all
   *   active        approved; reminders go out on this number
   *   failed        gave up; `statusDetail` says why and it can be retried
   */
  status: 'pending' | 'provisioning' | 'registering' | 'active' | 'failed';
  /** Why it failed or what it is waiting on. Shown to the owner verbatim. */
  statusDetail: string | null;
  /** E.164, once there is one. Null while the number is still being bought. */
  phoneNumber: string | null;
  /** 'sole_proprietor' | 'standard' | 'toll_free', once the kind is settled. */
  registrationKind: string | null;
  /** When the carriers approved it — the moment reminders could start. */
  activatedAt: string | null;
  requestedAt: string;
  /**
   * Sole owners confirm their mobile by replying YES to a text. True while
   * that reply is what we are waiting on.
   */
  awaitingMobileReply: boolean;
  /** Last four digits of that mobile, so the owner knows which phone to check. */
  mobileLast4: string | null;
  /**
   * The form they submitted, returned only when they can edit and resubmit.
   * No provider ids.
   */
  draft: ManagedNumberDraft | null;
  /**
   * Whether this sender lives on a Twilio account the installation no longer
   * uses, and so cannot send whatever its status says.
   *
   * Its own field rather than something the page infers, because the row looks
   * perfectly healthy from the outside: an operator who rotates accounts leaves
   * `active` rows whose credentials are simply gone, sending falls back to the
   * shared sender, and nothing in the status says why. It is also what tells
   * the panel to offer a replacement, which `requestManagedNumber` supports for
   * exactly this case.
   */
  stranded: boolean;
  /**
   * Whether this number can send right now: active, on the installation's
   * current Twilio account, and the company entitled to it. It is what sends
   * whenever the company's own Twilio account is absent or switched off.
   * While FEATURE_MANAGED_SMS is off, Settings keeps showing the number only
   * when this is true.
   */
  usable: boolean;
}

/** The business form, echoed back so a rejected request can be corrected. */
export interface ManagedNumberDraft {
  kind: 'standard' | 'sole_proprietor';
  legalName: string;
  ein: string | null;
  businessType: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  region: string;
  postalCode: string;
  website: string | null;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  mobilePhone: string | null;
  areaCode: string | null;
}

export interface CalendarFeed {
  id: string;
  label: string | null;
  userId: string | null;
  /** The one location this feed serves, or null for the company-wide feed. */
  locationId: string | null;
  createdAt: string;
  lastAccessedAt: string | null;
}

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------

/**
 * One API key as Settings sees it. Never the key itself: that exists only in
 * the response that mints it, and `hint` — its first few characters — is all
 * anybody can be shown afterwards.
 */
export interface ApiKey {
  id: string;
  name: string;
  scopes: ApiScope[];
  /** What the scope set is called when it is one of the two presets. */
  preset: ApiKeyPreset;
  hint: string;
  /** The user whose permissions cap this key. */
  userId: string;
  /** The one location it works in. */
  locationId: string;
  createdAt: string;
  createdByUserId: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

/** One row of the published integration surface, served with the key list. */
export interface ApiSurfaceEntry {
  method: string;
  path: string;
  scope: ApiScope;
  summary: string;
}

/** The scope vocabulary, served so the UI never hardcodes it. */
export interface ApiScopeDescriptor {
  scope: ApiScope;
  /** Its consequences leave the building: a send, a payment, a deletion. */
  sensitive: boolean;
  permissions: readonly string[];
}

/** An app a company has connected through OAuth (Settings → Connected apps). */
export interface ConnectedApp {
  id: string;
  clientId: string;
  /** The app's display name, from the deployment's client registry. */
  clientName: string;
  /**
   * `assistant` for ChatGPT, Claude and anything else connected through the
   * MCP server at /mcp; `integration` for Zapier, Make and configured apps.
   */
  kind: 'assistant' | 'integration';
  scopes: ApiScope[];
  locationId: string;
  userId: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

/**
 * An AI assistant connected to the account (Settings → ChatGPT & Claude).
 * Everybody sees their own; an owner sees everyone's.
 */
export interface AssistantConnection {
  id: string;
  /** "Claude", "ChatGPT", or what another assistant calls itself. */
  clientName: string;
  scopes: ApiScope[];
  locationId: string;
  userId: string;
  /** Whose connection it is — on an owner's list only, null otherwise. */
  userName: string | null;
  /** Whether it is the signed-in person's own. */
  mine: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

// ---------------------------------------------------------------------------
// Public (customer-facing) quote
// ---------------------------------------------------------------------------

export interface PublicQuoteRoomService {
  name: string;
  description: string | null;
  estimatedMinutes: number | null;
  customerNotes: string | null;
}

export interface PublicQuoteGroup {
  name: string;
  quantity: number;
  notes: string | null;
  /** Labor plus flat room charges for this group, times its quantity. Before discount and tax. */
  subtotalCents: number;
  /** Null when the quote hides estimated time. */
  minutes: number | null;
  rooms: PublicQuoteRoom[];
}

export interface PublicQuoteRoom {
  roomName: string;
  quantity: number;
  billableMinutes: number | null;
  /** This room's own price, when it has one. `null` when priced by time. */
  chargeCents: number | null;
  customerNotes: string | null;
  services: PublicQuoteRoomService[];
}

export interface PublicQuote {
  firstClean?: {
    options: Array<{
      id: string;
      name: string;
      /** `cardTotalCents`: the price with the online card fee, when one applies. */
      firstVisit: { priceCents: number; cardTotalCents?: number | null; tasks: string[] };
      routine: { priceCents: number; cardTotalCents?: number | null; tasks: string[] };
    }>;
    selectedId: string | null;
    /** Built from the quote's own first clean price: one price, nothing to choose. */
    fromQuote?: boolean;
  } | null;
  areaPricing?: { squareFeet: number; rateCents: number } | null;
  pricingBlocked?: boolean;
  quoteNumber: string;
  status: QuoteStatus;
  quoteDate: string;
  expirationDate: string | null;
  isExpired: boolean;
  /**
   * The timezone this quote is read in — the location's where it keeps its
   * own, the company's otherwise. The same one `isExpired` was decided with,
   * because a quote should not expire on one calendar and be dated on another.
   */
  timezone: string;
  /**
   * BCP 47 locale for amounts and dates on this document. Absent means the
   * quote language's locale, which is what every company used before a
   * company locale existed.
   */
  formatLocale?: string;
  /** Company date-style override, when one is set. */
  dateFormat?: DateFormatStyle | null;
  company: {
    name: string;
    /** Only set when it differs from the trading name (Settings promises this). */
    legalName: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    addressLines: string[];
    logoUrl: string | null;
    currencyCode: string;
  };
  customerName: string;
  propertyAddress: string | null;
  /**
   * Set only when the quote is actually grouped. A single untouched default
   * group leaves this absent so the page keeps listing rooms the way it always
   * has.
   */
  groups?: PublicQuoteGroup[];
  rooms: PublicQuoteRoom[];
  fees: Array<{
    name: string;
    description: string | null;
    amountCents: number;
  }>;
  customerNotes: string | null;
  terms: string | null;
  showTime: boolean;
  showHourlyRate: boolean;
  hourlyRateCents: number | null;
  /** The minutes charged by the hour, for the labor line. */
  billableMinutes: number | null;
  /** How long the whole visit takes, priced rooms included. */
  estimatedMinutes: number | null;
  laborSubtotalCents: number;
  /** Flat-priced rooms. Zero on a quote priced entirely by time. */
  roomChargesCents: number;
  /**
   * Whether any room carries a price at all — which is not the same question
   * as whether those prices sum to something. A room priced at zero is priced.
   */
  hasRoomCharges: boolean;
  fixedFeesCents: number;
  discountCents: number;
  /** The accepted tier's reduction. Zero until a tier is accepted. */
  frequencyDiscountCents: number;
  taxCents: number;
  totalCents: number;
  /**
   * The frequencies this quote is offered at, priced, in the canonical order —
   * empty unless the estimator switched the offer on. What the customer picks
   * from in the accept form.
   */
  frequencyTiers: Array<{
    frequency: SeriesFrequency;
    /** What a visit at this frequency costs. */
    totalCents: number;
    /** What the frequency saves against the quote as built. */
    frequencyDiscountCents: number;
  }>;
  /** The tier the acceptance recorded, when one was. */
  acceptedFrequency: SeriesFrequency | null;
  acceptedAt: string | null;
  /** Who accepted, as they typed it — the signature itself when one was required. */
  acceptedByName: string | null;
  declinedAt: string | null;
  canRespond: boolean;
  pdfAvailable: boolean;
  /**
   * Null unless the company charges a fee for paying online by card. When set,
   * the quote says so — the customer learns the surcharge exists while they are
   * deciding, not at the checkout page.
   */
  cardFee: {
    basisPoints: number;
    feeCents: number;
    totalWithFeeCents: number;
  } | null;
  /**
   * What kind of price a photo quote went out as (WILD-05): firm, with the
   * terms it was locked under, or a range the visit confirms. A range keeps
   * its width (± percent), so a price the customer can still choose, such as
   * a frequency, is shown as the band choosing it gives. Null on every other
   * quote.
   */
  pricePromise:
    | { kind: 'firm'; terms: string | null }
    | { kind: 'range'; lowCents: number; highCents: number; widthPct: number | null }
    | null;
}

// ---------------------------------------------------------------------------
// Quote requests (leads from the public intake form)
// ---------------------------------------------------------------------------

export interface QuoteRequest {
  id: string;
  /** Which location this record belongs to. */
  locationId: string | null;
  status: QuoteRequestStatus;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  propertyType: PropertyType | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  frequency: QuoteRequestFrequency | null;
  message: string | null;
  convertedCustomerId: string | null;
  convertedQuoteId: string | null;
  handledByUserId: string | null;
  handledAt: string | null;
  createdAt: string;
  /**
   * The AI draft proposal's state and headline, for the inbox row. `ai` is
   * null until (unless) a proposal is ready; `aiStatus` distinguishes "still
   * working", "tried and failed", and "the settings said no" from "never
   * attempted" (null, e.g. rows predating the feature).
   */
  aiStatus: 'pending' | 'ready' | 'failed' | 'skipped' | null;
  ai: {
    summary: string;
    confidence: 'low' | 'medium' | 'high';
    photoNotes: string | null;
    roomCount: number;
  } | null;
  /** How many photos the visitor attached. */
  photoCount: number;
  /** Which page the lead came through: the request form or the photo-quote page. */
  source: QuoteRequestSource;
  /** The photo quote behind a `photo_dump` lead; null for every other lead. */
  photoIntake: QuoteRequestPhotoIntake | null;
}

// ---------------------------------------------------------------------------
// AI assists
// ---------------------------------------------------------------------------

export type InboundTextIntent = 'confirm' | 'reschedule' | 'cancel' | 'question' | 'other';
export type InboundTextStatus = 'new' | 'handled' | 'dismissed';

/** An inbound customer text in the SMS inbox (GET /inbound-texts). */
export interface InboundText {
  id: string;
  locationId: string | null;
  customerId: string | null;
  customerName: string | null;
  /** The upcoming visit the classifier believes the message refers to. */
  appointmentId: string | null;
  fromPhone: string;
  body: string;
  /** Null = not classified (toggle off, allowance spent, or model failed). */
  intent: InboundTextIntent | null;
  aiSummary: string | null;
  status: InboundTextStatus;
  handledByUserId: string | null;
  handledAt: string | null;
  createdAt: string;
}

export type AiSuggestionStatus = 'open' | 'accepted' | 'dismissed';

/** A reviewable tuning suggestion (GET /ai/suggestions). */
export interface AiSuggestion {
  id: string;
  kind: string;
  roomTemplateId: string | null;
  serviceTemplateId: string | null;
  /** Resolved names, for display; null when the template has since gone. */
  roomTemplateName: string | null;
  serviceTemplateName: string | null;
  currentMinutes: number | null;
  suggestedMinutes: number | null;
  rationale: string;
  status: AiSuggestionStatus;
  decidedByUserId: string | null;
  decidedAt: string | null;
  createdAt: string;
}

/** The Settings meter (GET /ai/usage). */
export interface AiUsageSummary {
  /** False = the deployment has no model key; every assist is off. */
  available: boolean;
  /** 'YYYY-MM' (UTC). */
  month: string;
  /** The plan's monthly credit allowance. */
  allowance: number;
  /** The most this company may spend in any one day — the burst cap. */
  dailyAllowance: number;
  /** Credits spent this month, and the same split by action. */
  used: number;
  byAction: Record<string, number>;
}

export interface ProviderUsageMeter {
  currency: 'USD';
  allowanceUsdMicros: number;
  committedUsdMicros: number;
  uncertainUsdMicros: number;
  segmentAllowance?: number;
  segmentsUsed?: number;
}
export type ProviderUsageSummary =
  | { active: false }
  | {
      active: true;
      month: string;
      resetAt: string | null;
      companyPooled: boolean;
      noAutomaticOverageCharges: true;
      ai: ProviderUsageMeter;
      help: ProviderUsageMeter;
      sms: ProviderUsageMeter;
      requiredNotices: ProviderUsageMeter;
      smsPayer: 'platform' | 'company' | 'unconfigured';
    };

// ---------------------------------------------------------------------------
// Magic Quote (GET /magic-quotes — quotes:read)
// ---------------------------------------------------------------------------

/** One entry on the walkthrough timeline. */
export interface MagicQuoteCapture {
  id: string;
  /**
   * The id the device minted for this capture, or null for one created
   * server-side (a sampled video frame). What lets a client tell its own local
   * preview apart from the server's copy of the same photo.
   */
  clientCaptureId: string | null;
  type: CaptureType;
  /** Filed under this walkthrough room, once the pipeline has grouped it. */
  roomId: string | null;
  roomName: string | null;
  /** Present for photo, video and audio; null once retention has expired. */
  mediaUrl: string | null;
  contentType: string | null;
  durationSeconds: number | null;
  /** What the cleaner typed, or the room a marker names. */
  text: string | null;
  /** What was heard, with `[12.5s]` marks. Null until transcription runs. */
  transcription: string | null;
  capturedAt: string;
  sequence: number;
  status: CaptureStatus;
  /** Why this capture could not be read, when it could not. */
  error: string | null;
  flagged: boolean;
  /**
   * Sampled frames of this video that failed. Always 0 for other types.
   *
   * The clip can be `processed` — its audio split fine — while the frames
   * carrying everything the camera saw all failed. Without this the timeline
   * had no way to say so, because frames themselves are never shown.
   */
  failedFrameCount: number;
}

/** A reviewable claim, with the evidence behind it. */
export interface MagicQuoteObservation {
  id: string;
  type: ObservationType;
  label: string;
  detail: string | null;
  confidence: number;
  visibility: ObservationVisibility;
  reviewStatus: ObservationReviewStatus;
  roomId: string | null;
  /** True while this is below the confidence threshold and unreviewed. */
  needsReview: boolean;
  /** Set when a later claim in the same walkthrough replaced this one. */
  supersededById: string | null;
  evidence: MagicQuoteEvidence[];
}

export interface MagicQuoteEvidence {
  captureId: string;
  /** Seconds into the recording, so the player can open at the moment. */
  offsetSeconds: number | null;
  excerpt: string | null;
}

export interface MagicQuoteRoom {
  id: string;
  name: string;
  /** Null means no library room type fits, so this room cannot be priced. */
  roomTemplateId: string | null;
  sizeCode: string | null;
  conditionCode: string | null;
  estimatedMinutes: number | null;
  excluded: boolean;
  notes: string | null;
  /** 'manual' once a person has corrected it; re-synthesis then leaves it be. */
  source: 'ai' | 'manual';
  captureCount: number;
  /**
   * Why this room will not reach the quote, in the same words generation would
   * use — or null when it will price.
   *
   * Answered on the way out rather than discovered on the way in: an estimator
   * who presses Generate and is told "no rooms could be priced" is looking at
   * rooms they can see, and nothing on the screen says which one is the
   * problem or what to do about it. On the row, beside the room it is about,
   * it is a sentence with an obvious next move.
   */
  pricingProblem: string | null;
}

export interface MagicQuoteMessage {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  /** What this turn changed, in plain language. Empty for an answer. */
  applied: string[];
  createdAt: string;
}

export interface MagicQuoteCoverageSuggestion {
  roomTemplateId: string;
  name: string;
}

/**
 * Versioned Room Check for a v1 walkthrough. `null` on a legacy session.
 * Suggestions are optional rooms, never work silently added by the server.
 */
export interface MagicQuoteCoverage {
  protocolVersion: 1;
  version: number;
  confirmed: boolean;
  confirmedAt: string | null;
  gaps: string[];
  suggestions: MagicQuoteCoverageSuggestion[];
  canConfirm: boolean;
}

export interface MagicQuoteSessionSummary {
  id: string;
  /** Null while the walkthrough has nobody on file yet. */
  customerId: string | null;
  /** Null for the same reason — the UI says "no customer yet", not "Unknown". */
  customerName: string | null;
  propertyId: string | null;
  propertyAddress: string | null;
  quoteId: string | null;
  mode: MagicQuoteMode;
  status: MagicQuoteStatus;
  aiStatus: MagicQuoteAiStatus;
  /**
   * Why the last pass failed, in words meant for the estimator. Null unless
   * `aiStatus` is `failed` — and cleared the moment it is not, so a
   * walkthrough that organizes on the second attempt shows no apology.
   */
  aiError: string | null;
  conversationCapture: boolean;
  summary: string | null;
  /** 0-100, advisory. Null until the walkthrough has been organized. */
  readinessScore: number | null;
  captureCount: number;
  /**
   * AI credits this walkthrough has been charged so far — the same unit the
   * monthly allowance is counted in, so the two numbers compare directly.
   *
   * Per walkthrough rather than per month because this is the one feature
   * whose cost varies with the size of the job — see docs/ai-credits.md.
   */
  billedCredits: number;
  startedAt: string;
  completedAt: string | null;
}

/** The full walkthrough: timeline, rooms, claims and the conversation. */
export interface MagicQuoteSession extends MagicQuoteSessionSummary {
  captures: MagicQuoteCapture[];
  rooms: MagicQuoteRoom[];
  observations: MagicQuoteObservation[];
  /** Null is the intentional compatibility state for pre-v1 clients. */
  coverage: MagicQuoteCoverage | null;
  messages: MagicQuoteMessage[];
  /** What the pipeline could not settle, for the review screen. */
  openQuestions: string[];
  /** Frequency the walkthrough landed on, where it landed on one. */
  frequency: 'one_time' | 'weekly' | 'biweekly' | 'monthly' | null;
  /** Captures still waiting to be read, and ones that could not be. */
  pendingCaptureCount: number;
  /**
   * Whether a reconciliation is running right now.
   *
   * The walkthrough's own claim, not `aiStatus` — draining the capture queue
   * raises that too, and the review screen has to tell "the model is deciding
   * the rooms" apart from "photos are still being read". Both mean the page is
   * not ready to be shown, but only one of them is nearly over.
   */
  organizing: boolean;
  failedCaptureCount: number;
  /** Plain-language gaps behind the readiness score. */
  gaps: string[];
  /**
   * Who the walkthrough thinks this job is for, when it started without a
   * customer. Null once one has been chosen, and until synthesis has run.
   */
  customerDraft: MagicQuoteCustomerDraft | null;
  /** False when this deployment cannot turn recordings into text. */
  speechRecognitionAvailable: boolean;
}

/** What a walkthrough's model calls have cost so far (admin/debug view). */
export interface MagicQuoteCostSummary {
  inputTokens: number;
  outputTokens: number;
  images: number;
  mediaSeconds: number;
  calls: number;
  failures: number;
}

// ---------------------------------------------------------------------------
// Dashboard (GET /dashboard — reports:read)
// ---------------------------------------------------------------------------

/**
 * A sum of quote values. `totalCents` adds the stored totals, as it always
 * has; `lowCents` and `highCents` add each range quote's band ends instead of
 * the middle it stores, and each other quote's total. They equal `totalCents`
 * unless a range quote is in the sum, and the band is what to show then.
 */
export interface QuoteValueSum {
  totalCents: number;
  lowCents: number;
  highCents: number;
}

export interface DashboardPipelineRow extends QuoteValueSum {
  status: QuoteStatus;
  count: number;
}

export interface DashboardData {
  /** Open quotes by stored status, with derived-expired split out. */
  pipeline: DashboardPipelineRow[];
  /** Accepted quotes awaiting completion. */
  accepted: { count: number } & QuoteValueSum;
  /** Customer decisions over the trailing 90 days. */
  decisions90d: { accepted: number; declined: number };
  /** Succeeded charges minus succeeded refunds, trailing 30 days. */
  collected30dCents: number;
  /**
   * Tips customers added on the window's succeeded charges — gross, not net:
   * a refund nets out of `collected30dCents`, but which part of a partial
   * refund was "the tip" is unknowable, so refunds are never attributed back
   * to tips (refund rows carry `tipCents: 0` by design).
   */
  tips30dCents: number;
  /** Visits on the calendar from today through six days out. */
  weekAhead: { visits: number; scheduledMinutes: number };
  needsAttention: {
    /** Open quotes whose expiration date falls within the next 7 days. */
    expiringSoon: number;
    /** Upcoming visits where the customer asked for a different time. */
    changeRequested: number;
    /** Visits whose ledger currently rolls up to a failed payment. */
    failedPayments: number;
    /** Quote requests nobody has reviewed yet. */
    newQuoteRequests: number;
  };
  /** Estimated-vs-actual across completed visits that recorded a duration. */
  timeAccuracy: {
    trackedVisits: number;
    scheduledMinutes: number;
    actualMinutes: number;
  };
}

// ---------------------------------------------------------------------------
// Sales dashboard (GET /sales-dashboard — quotes:read)
// ---------------------------------------------------------------------------

/** One open quote on the sales dashboard's oldest-first list. */
export interface SalesDashboardQuote {
  id: string;
  quoteNumber: string;
  customerName: string;
  status: QuoteStatus;
  totalCents: number;
  /** The band a range quote promised; both equal `totalCents` for any other quote. */
  lowCents: number;
  highCents: number;
  /** The currency the quote was priced in, which may predate a currency change. */
  currencyCode: string;
  /** When it went to the customer; the age is counted from here. */
  sentAt: string;
}

/**
 * The quote figures an estimator works from, for one scope: the quotes this
 * person wrote, or every quote at the location.
 */
export interface SalesDashboardFigures {
  /**
   * Sent or viewed, not decided and not expired, with how long each has been
   * waiting since it was sent: 0–3, 4–7, 8–14 and 15 or more days.
   */
  open: QuoteValueSum & {
    count: number;
    ages: { days0to3: number; days4to7: number; days8to14: number; days15plus: number };
    /** The five that have waited longest. */
    oldest: SalesDashboardQuote[];
  };
  /** Open quotes whose expiration date falls within the next 7 days. */
  expiringSoon: { count: number } & QuoteValueSum;
  /** Viewed by the customer three or more days ago, still without an answer. */
  followUpsDue: number;
  /** Customer decisions over the trailing 90 days. */
  decisions90d: { accepted: number; declined: number };
  /**
   * Quotes sent since the first of this month, on the location's calendar.
   * `totalCents` sums only the quotes priced in the current currency, and
   * `pricedCount` is how many those are — the divisor for an average.
   */
  sentThisMonth: { count: number; pricedCount: number } & QuoteValueSum;
}

export interface SalesDashboardData {
  /** The location's currency; every money figure is in it. */
  currencyCode: string;
  mine: SalesDashboardFigures;
  location: SalesDashboardFigures;
  /** Accepted at the location and not yet booked onto the schedule. */
  readyToBook: { count: number } & QuoteValueSum;
  /** Web-form and photo requests at the location nobody has handled yet. */
  newRequests: number;
}

// ---------------------------------------------------------------------------
// Reports (GET /reports/* — reports:read, except MyWeek which is per-user)
// ---------------------------------------------------------------------------

/** Inclusive company-local calendar dates bounding a report. */
export interface ReportRange {
  from: string;
  to: string;
}

/**
 * One site's month, costed: what the work earned, what it cost in crew time,
 * and the gap. The row a janitorial owner scans for negatives.
 */
export interface JobCostingRow {
  /** `YYYY-MM`, the month the visits were scheduled in. */
  month: string;
  propertyId: string | null;
  siteName: string | null;
  siteCode: string | null;
  customerId: string;
  customerName: string;
  completedVisits: number;
  /** Visits that recorded actual minutes — how much of the month is costed. */
  trackedVisits: number;
  scheduledMinutes: number;
  /** Time on site: first person in to last person out, summed over the visits. */
  actualMinutes: number;
  /**
   * Person-minutes worked — every crew member's time, not the visit's span.
   * The figure the labor cost is charged against; equal to `actualMinutes`
   * only where one person worked every visit in the row.
   */
  laborMinutes: number;
  /**
   * What the client was billed, in the company's currency. After sales tax
   * (and less refunds) when Team pay costed the row. The agreed price when
   * it did not.
   */
  revenueCents: number;
  /**
   * Wages plus the W-2 payroll load: the same loaded labor cost as Job profit
   * and the accountant export. The hourly backup covers minutes Team pay
   * cannot cost. Null when some of those minutes have no backup either.
   */
  laborCostCents: number | null;
  marginCents: number | null;
  /** Margin as a share of revenue. Null when there is no revenue to divide. */
  marginBasisPoints: number | null;
  losingMoney: boolean;
}

export interface JobCostingReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  /**
   * The hourly backup from Company settings, used when Team pay is not set up
   * or a person's pay is missing. Null when the field is blank. Rows can
   * still carry a loaded labor cost from Team pay.
   */
  laborCostCentsPerHour: number | null;
  rows: JobCostingRow[];
  truncated: boolean;
  /** Across the rows SHOWN — a truncated margin total would mislead. */
  totals: {
    completedVisits: number;
    trackedVisits: number;
    actualMinutes: number;
    laborMinutes: number;
    revenueCents: number;
    laborCostCents: number | null;
    marginCents: number | null;
  };
}

export interface ProfitabilityGroupRow {
  /** Customer id for byCustomer rows, series id for bySeries rows. */
  id: string;
  customerName: string;
  /** Only on bySite rows: the building, and the client's own code for it. */
  siteName: string | null;
  siteCode: string | null;
  /** Only on bySeries rows. */
  frequency: SeriesFrequency | null;
  completedVisits: number;
  /** Completed visits that recorded actual minutes — the rate's denominator. */
  trackedVisits: number;
  scheduledMinutes: number;
  /** Time on site: first person in to last person out, summed over the visits. */
  actualMinutes: number;
  /**
   * Person-minutes worked — every crew member's time, not the visit's span.
   * What the realized rate is divided by; equal to `actualMinutes` only where
   * one person worked every visit in the row.
   */
  laborMinutes: number;
  /** Agreed visit prices summed over every completed visit. */
  billedCents: number;
  /** Agreed prices summed over tracked visits only — the rate's numerator. */
  trackedBilledCents: number;
  /**
   * What these visits have actually SETTLED: each succeeded charge's
   * principal — its amount less tip and card fee — reduced by that charge's
   * own refunds and floored at zero. Never negative, and never inflated by a
   * tip, so it can be compared against `billedCents`.
   */
  collectedCents: number;
  /** trackedBilledCents per PERSON-hour worked; null until a visit is tracked. */
  realizedRateCentsPerHour: number | null;
  /** True when the realized rate is under the company's target rate. */
  belowTarget: boolean;
}

export interface ProfitabilityReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  /** The company's default hourly rate — what "below target" compares against. */
  targetRateCentsPerHour: number;
  totals: {
    completedVisits: number;
    trackedVisits: number;
    scheduledMinutes: number;
    actualMinutes: number;
    laborMinutes: number;
    billedCents: number;
    trackedBilledCents: number;
    collectedCents: number;
    realizedRateCentsPerHour: number | null;
  };
  /** The largest groups by billed value; totals above always cover everything. */
  byCustomer: ProfitabilityGroupRow[];
  bySeries: ProfitabilityGroupRow[];
  /**
   * One row per building. The grain a multi-site client is managed at: the
   * account can be comfortably profitable while two of its ten buildings
   * quietly lose money every month.
   */
  bySite: ProfitabilityGroupRow[];
  /** True when more groups exist than the lists carry. */
  byCustomerTruncated: boolean;
  bySeriesTruncated: boolean;
  bySiteTruncated: boolean;
}

export interface RetentionBucket {
  series: number;
  /** Visit prices normalized to a monthly figure (weekly ×52/12, biweekly ×26/12). */
  monthlyRunRateCents: number;
}

export interface WinBackRow {
  customerId: string;
  customerName: string;
  lastVisitDate: string;
  completedVisits: number;
  avgVisitPriceCents: number;
}

export interface RetentionReport extends PaginatedReport {
  currencyCode: string;
  active: RetentionBucket;
  paused: RetentionBucket;
  /** Series whose status became `ended` inside the window. */
  endedInWindow: RetentionBucket & { window: ReportRange };
  /**
   * Customers who have at least one completed visit but no open future visit
   * and no active or paused schedule — the win-back call list, most recently
   * lost first.
   */
  winBack: WinBackRow[];
}

export interface QuoteFunnelStalledRow {
  quoteId: string;
  quoteNumber: string;
  customerName: string;
  totalCents: number;
  /** The band a range quote promised; both equal `totalCents` for any other quote. */
  lowCents: number;
  highCents: number;
  /** When the stage it is stuck at was entered. */
  sinceAt: string;
}

export interface QuoteFunnelWeekRow {
  /** Monday of the week, ISO date. */
  weekStart: string;
  sent: number;
  accepted: number;
  declined: number;
}

export interface QuoteFunnelReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  /** Quotes created in the range, whether or not they were sent. */
  drafted: { count: number } & QuoteValueSum;
  /** The cohort: quotes SENT inside the range; buckets are its current state. */
  sent: { count: number } & QuoteValueSum;
  accepted: { count: number } & QuoteValueSum;
  declined: { count: number } & QuoteValueSum;
  /** Sent, viewed, no decision yet, not past expiry. */
  viewedPending: { count: number } & QuoteValueSum;
  /** Sent, never viewed, not past expiry. */
  unviewedPending: { count: number } & QuoteValueSum;
  /** Sent and undecided but past the expiration date. */
  expired: { count: number } & QuoteValueSum;
  /** Sent, then closed without a customer decision (canceled or completed early). */
  closedOther: { count: number } & QuoteValueSum;
  medianHoursToView: number | null;
  medianHoursToDecision: number | null;
  weekly: QuoteFunnelWeekRow[];
  /** Open quotes worth a nudge: sent >3 days, never viewed. */
  sentNotViewed: QuoteFunnelStalledRow[];
  /** Open quotes the customer read >3 days ago and left unanswered. */
  viewedNoAnswer: QuoteFunnelStalledRow[];
  /**
   * The declines in the cohort, by the reason the customer gave — most common
   * first. `reason: null` is the row for declines with none recorded, which
   * is every decline from before the question was asked and every customer
   * who preferred not to answer it.
   */
  declineReasons: QuoteFunnelDeclineReasonRow[];
}

export interface QuoteFunnelDeclineReasonRow extends QuoteValueSum {
  reason: QuoteDeclineReason | null;
  count: number;
}

export type ReceivablesAgingBucket = '0_7' | '8_30' | '31_60' | 'over_60';

export interface ReceivableVisitRow {
  appointmentId: string;
  customerId: string;
  customerName: string;
  scheduledDate: string;
  completedAt: string | null;
  priceCents: number;
  outstandingCents: number;
  paymentStatus: AppointmentPaymentStatus;
}

export interface ReceivablesReport extends PaginatedReport {
  failedSummary?: { visits: number; totalCents: number };
  currencyCode: string;
  outstanding: { visits: number; totalCents: number };
  aging: Array<{
    bucket: ReceivablesAgingBucket;
    visits: number;
    totalCents: number;
  }>;
  /** Completed visits whose ledger currently rolls up to failed. */
  failed: ReceivableVisitRow[];
  /** Largest balances first. */
  topDebtors: Array<{
    customerId: string;
    customerName: string;
    visits: number;
    outstandingCents: number;
  }>;
  /** Over paid visits completed in the trailing 90 days. */
  daysToPay: {
    paidVisits: number;
    avgDays: number | null;
    medianDays: number | null;
  };
  /** Succeeded charge volume by method, trailing 30 days. */
  methodMix30d: Array<{ method: PaymentMethodKind; amountCents: number }>;
  /** Every visit with a balance, oldest first — the CSV's rows. */
  openItems: ReceivableVisitRow[];
}

export interface CrewMemberRow {
  /** Null on the synthetic unassigned row. */
  userId: string | null;
  name: string;
  completedVisits: number;
  scheduledMinutes: number;
  actualMinutes: number;
  /** Completed visits that recorded actual minutes. */
  trackedVisits: number;
  /** Scheduled minutes over tracked visits only, for a fair accuracy ratio. */
  trackedScheduledMinutes: number;
  /** Visit prices split evenly across each visit's crew. */
  attributedRevenueCents: number;
  cancelledVisits: number;
  noShowVisits: number;
}

export interface CrewReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  members: CrewMemberRow[];
  /**
   * The range's visits counted ONCE each — the member rows fan out per crew
   * member, so summing them would count a three-person job three times.
   */
  totals: {
    /** Full filtered person count, excluding the unassigned row. */
    memberCount?: number;
    actualMinutes?: number;
    attributedRevenueCents?: number;
    completedVisits: number;
    cancelledVisits: number;
    noShowVisits: number;
  };
}

export interface CapacityMemberRow {
  userId: string;
  name: string;
  /** From the member's working hours (their own, or the location/company default). */
  availableMinutes: number;
  /** Open visits they are named on inside the week. */
  bookedMinutes: number;
  /** Rounded percent; null when availableMinutes is zero. */
  utilizationPercent: number | null;
}

export interface CapacityWeekRow {
  weekStart: string;
  weekEnd: string;
  members: CapacityMemberRow[];
  /** Minutes of open visits with nobody assigned — work owed with no owner. */
  unassignedMinutes: number;
  totalAvailableMinutes: number;
  totalBookedMinutes: number;
}

export interface CapacityReport extends PaginatedReport {
  weeks: CapacityWeekRow[];
}

export interface LeadRow {
  id: string;
  name: string;
  status: QuoteRequestStatus;
  createdAt: string;
  handledAt: string | null;
  hoursToTouch: number | null;
}

export interface LeadsReport extends PaginatedReport {
  range: ReportRange;
  counts: { total: number; new: number; converted: number; dismissed: number };
  /** converted ÷ (converted + dismissed); null before any lead is decided. */
  conversionRatePercent: number | null;
  medianHoursToFirstTouch: number | null;
  avgHoursToFirstTouch: number | null;
  weekly: Array<{ weekStart: string; received: number; converted: number }>;
  /** Untouched leads, oldest first — the ones going cold. */
  untouched: LeadRow[];
}

export interface MyWeekVisitRow {
  appointmentId: string;
  scheduledDate: string;
  startsAt: string;
  customerName: string;
  status: AppointmentStatus;
  durationMinutes: number;
  actualMinutes: number | null;
  priceCents: number | null;
}

/** The signed-in person's own week — every figure is theirs alone. */
export interface MyWeekReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  completedVisits: number;
  upcomingVisits: number;
  scheduledMinutes: number;
  actualMinutes: number;
  /** Charges this person recorded at the door (tap to pay + manual tenders). */
  collectedCents: number;
  visits: MyWeekVisitRow[];
}

// ---------------------------------------------------------------------------
// Team pay and job profit (migration 0035 — reports:read, owner and manager)
// ---------------------------------------------------------------------------

/** Settings → Team pay. Never on `Company`, which every role can read. */
export interface TeamPaySettings {
  /** The payroll load on W-2 pay. Null = not set: no load is added. */
  defaultBurdenBasisPoints: number | null;
  /** Pro: the margin quotes are measured against. Null = no target. */
  targetMarginBasisPoints: number | null;
  /** Pro: supplies as a share of a job's price. Null = not costed. */
  suppliesBasisPoints: number | null;
  ownerCostEnabled: boolean;
  ownerHourlyCents: number | null;
  /** What a card payment is estimated to cost: a share plus a fixed amount. */
  cardProcessingBasisPoints: number;
  cardProcessingFixedCents: number;
  /** Pro: email last month's accountant export to the owner on the 1st. */
  accountantExportEmailEnabled: boolean;
}

export interface TeamPayRate {
  id: string;
  payType: PayType;
  rateCents: number | null;
  rateBasisPoints: number | null;
  annualSalaryCents: number | null;
  salaryHoursPerYear: number | null;
  /** The first company-local date it applies to. */
  effectiveFrom: string;
  createdAt: string;
}

export interface TeamPayMember {
  userId: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  /** Owners are costed by the company's owner setting, not by their own rates. */
  isOwner: boolean;
  workerType: WorkerType | null;
  burdenOverrideBasisPoints: number | null;
  /** The W-2 load that applies: the override, else the company default. */
  effectiveBurdenBasisPoints: number | null;
  /** When their W-9 came in. Only a date: the form itself is never stored. */
  w9ReceivedOn: string | null;
  /** The rate in force today, if any. */
  currentRate: TeamPayRate | null;
  /** Every rate, newest first — the history a backdated change edits. */
  rates: TeamPayRate[];
}

export interface TeamPayOverview {
  settings: TeamPaySettings;
  members: TeamPayMember[];
  /** Whether the plan carries the Pro settings (target, supplies, export email). */
  insights: boolean;
  /** The company-local date "in force today" was read against. */
  today: string;
  /**
   * Hours-weighted average of hourly and salaried staff, with the W-2 payroll
   * load and nothing added for 1099. Null when nobody is paid that way. The
   * hourly backup in Company settings can apply it in one tap.
   */
  suggestedHourlyCents: number | null;
}

/** One worker's part in a job's labor cost. */
export interface VisitProfitLine {
  userId: string;
  name: string;
  workerType: WorkerType | null;
  payType: PayType | 'owner' | null;
  /** The paid minutes the line was costed on. */
  minutes: number;
  /** Time worked unpaid (a ride-along): no cost, but time on the visit. */
  unpaidMinutes: number;
  minutesSource: 'clock' | 'typed' | 'scheduled';
  baseCents: number;
  burdenCents: number;
  status: 'costed' | 'pay_missing' | 'owner_free' | 'unpaid';
  approximate: boolean;
  timeIncomplete: boolean;
}

export type ProfitUpdateReason = 'time' | 'rate' | 'payment' | 'price' | 'late_fee';

/** The Job profit card on a visit. */
export interface VisitProfitSummary {
  appointmentId: string;
  /**
   * `actual` for a completed visit (or one canceled with a late fee);
   * `estimate` for one not done yet — the price and the scheduled hours.
   */
  kind: 'actual' | 'estimate';
  currencyCode: string;
  revenueCents: number;
  laborCents: number;
  burdenCents: number;
  feeCents: number;
  suppliesCents: number;
  profitCents: number;
  /** Null when there was no revenue to divide. */
  marginBasisPoints: number | null;
  /** Person-minutes worked on the visit, paid or not: set against the expected ones. */
  laborMinutes: number;
  /** The scheduled length × the crew: the hours the job was expected to take. */
  estimatedLaborMinutes: number | null;
  estimatedProfitCents: number | null;
  estimatedMarginBasisPoints: number | null;
  lines: VisitProfitLine[];
  /**
   * An estimate's crew spots nobody is assigned to yet: a visit booked for
   * three with one person on it has two. They are costed at the team's
   * average pay, in the labor above but in nobody's line. Zero otherwise.
   */
  openSlots: number;
  payMissing: boolean;
  /** Somebody paid by the hour has no recorded time: the figure is estimated. */
  timeIncomplete: boolean;
  /**
   * Completed with nobody assigned or clocked in: who did the work is not
   * recorded, so its labor cost — and its profit — is not known.
   */
  crewMissing: boolean;
  /** A salary's hourly equivalent is in the figure. */
  approximate: boolean;
  /** The revenue is a late cancellation fee. */
  lateCancelFee: boolean;
  /** Pro: the company's target, for coloring the margin. */
  targetMarginBasisPoints: number | null;
  /** When a recomputation last changed the numbers, and why; null if never. */
  updatedAt: string | null;
  updatedReason: ProfitUpdateReason | null;
  /**
   * True when there is no stored snapshot yet — a visit completed before
   * this feature — and the figures are computed now, from today's settings.
   */
  live: boolean;
}

/** One completed visit in the job profit report. */
export interface JobProfitRow {
  appointmentId: string;
  scheduledDate: string;
  customerId: string;
  customerName: string;
  /** The schedule's frequency; one-time work is a service of its own. */
  serviceType: 'one_time' | 'weekly' | 'biweekly' | 'monthly' | null;
  crew: Array<{ userId: string; name: string }>;
  revenueCents: number;
  laborCents: number;
  burdenCents: number;
  feeCents: number;
  suppliesCents: number;
  profitCents: number;
  marginBasisPoints: number | null;
  estimatedMarginBasisPoints: number | null;
  laborMinutes: number;
  estimatedLaborMinutes: number | null;
  payMissing: boolean;
  timeIncomplete: boolean;
  /** Completed with nobody assigned or clocked: who did the work, and so its labor, is unknown. */
  crewMissing: boolean;
  approximate: boolean;
  lateCancelFee: boolean;
}

/** One customer's jobs added up — which recurring customers are underpriced. */
export interface JobProfitCustomerRow {
  customerId: string;
  customerName: string;
  visits: number;
  revenueCents: number;
  profitCents: number;
  marginBasisPoints: number | null;
  estimatedMarginBasisPoints: number | null;
  belowTarget: boolean;
}

/**
 * A run of jobs that keeps landing below its estimate: the hours run over,
 * or the margin comes in under what the quote expected.
 */
export interface JobProfitPattern {
  customerId: string | null;
  customerName: string | null;
  serviceType: JobProfitRow['serviceType'];
  visits: number;
  /** Actual over estimated labor minutes, less one: 2500 = 25% over. */
  overrunBasisPoints: number | null;
  /** Average actual margin less average estimated margin (negative = below). */
  marginGapBasisPoints: number;
}

export interface JobProfitReport extends PaginatedReport {
  range: ReportRange;
  currencyCode: string;
  targetMarginBasisPoints: number | null;
  filters: { userId: string | null; customerId: string | null; serviceType: JobProfitRow['serviceType'] };
  totals: {
    visits: number;
    /** Visits whose numbers count: every worker costed and every hour recorded. */
    countedVisits: number;
    revenueCents: number;
    profitCents: number;
    /** Over the counted visits only; null when there are none. */
    averageMarginBasisPoints: number | null;
    /** Visits left out of the averages until their pay, time or crew is fixed. */
    payMissingVisits: number;
    timeIncompleteVisits: number;
    crewMissingVisits: number;
    /** Visits recorded in a currency the company no longer uses: not listed or added up. */
    otherCurrencyVisits: number;
  };
  /** The five counted visits with the lowest margin. */
  lowest: JobProfitRow[];
  rows: JobProfitRow[];
  truncated: boolean;
  byCustomer: JobProfitCustomerRow[];
  patterns: JobProfitPattern[];
  /** Who and whom the filters can name. */
  crewOptions: Array<{ userId: string; name: string }>;
  customerOptions: Array<{ customerId: string; customerName: string }>;
}

/** One contractor's year for the accountant: what they were paid and whether a W-9 is on file. */
export interface ContractorSummaryRow {
  userId: string;
  name: string;
  visits: number;
  totalPaidCents: number;
  w9ReceivedOn: string | null;
}

export interface ContractorSummary extends PaginatedReport {
  year: number;
  currencyCode: string;
  rows: ContractorSummaryRow[];
  /** Visits this year whose 1099 pay could not be costed (no rate in force). */
  payMissingVisits: number;
}

/** What the quote screen needs to estimate a quote's profit as it is edited. */
export interface QuoteProfitInputs {
  currencyCode: string;
  /**
   * Who the estimate costs: the crew usually on this customer's jobs, or —
   * when that is not known — every costable member, averaged per crew slot.
   */
  source: 'usual_crew' | 'team_average' | 'none';
  crew: QuoteProfitWorker[];
  team: QuoteProfitWorker[];
  owner: { enabled: boolean; hourlyCents: number | null };
  suppliesBasisPoints: number | null;
  /** Null when the customer does not usually pay by card. */
  cardFee: { basisPoints: number; fixedCents: number; surchargeBasisPoints: number } | null;
  targetMarginBasisPoints: number | null;
  /** Repeated patterns for this customer, for one line of context. */
  patterns: JobProfitPattern[];
}

/** A worker's pay terms as the quote estimate uses them. */
export interface QuoteProfitWorker {
  userId: string;
  name: string;
  isOwner: boolean;
  workerType: WorkerType | null;
  burdenBasisPoints: number;
  rate: {
    payType: PayType;
    rateCents: number | null;
    rateBasisPoints: number | null;
    annualSalaryCents: number | null;
    salaryHoursPerYear: number | null;
    effectiveFrom: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Logs (GET /logs/* — logs:read, owner only)
// ---------------------------------------------------------------------------

export interface ActivityEvent {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  summary: string | null;
  /** Display name of the acting user; null for customer/system actions. */
  userName: string | null;
  /**
   * Set when the change was made by a support operator signed in as that user
   * rather than by the user themselves — their email address. Null for
   * everything the company did itself, which is nearly every row.
   */
  impersonatedBy: string | null;
  /**
   * The API key or connected app the change was made through, where it was
   * made by one rather than by a person at a screen. Null for nearly every
   * row; set, it means the named credential acted with this user's authority.
   */
  viaApiKey: { id: string; name: string } | null;
  createdAt: string;
}

/**
 * One attempt from the email ledger (GET /logs/email). One entry per attempt,
 * including the refusals that never reached a provider — `status` says which.
 */
export interface EmailLogEntry {
  id: string;
  emailType: string;
  recipient: string;
  status: string;
  failureReason: string | null;
  quoteId: string | null;
  /** When the provider accepted it; null for attempts that never got that far. */
  sentAt: string | null;
  createdAt: string;
}

/** One attempt from the SMS ledger (GET /logs/sms). The body is never stored. */
export interface SmsLogEntry {
  id: string;
  messageType: string;
  recipient: string;
  status: string;
  failureReason: string | null;
  /** Billed message segments — what the provider invoice actually counts. */
  segments: number;
  quoteId: string | null;
  customerId: string | null;
  appointmentId: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Service requests (migration 0093)
// ---------------------------------------------------------------------------

/**
 * A client's complaint or ask, and the clock on answering it.
 *
 * `acknowledgedAt` is the number that matters, not `resolvedAt`: a bin
 * problem fixed at the next visit three days later is fine if somebody
 * answered within the hour, and a disaster if nobody said anything at all.
 */
export interface ServiceRequest {
  id: string;
  customerId: string;
  propertyId: string | null;
  kind: ServiceRequestKind;
  subject: string;
  body: string | null;
  raisedByName: string | null;
  status: ServiceRequestStatus;
  /** The promise in force when it was raised, not the one in force today. */
  responseTargetHours: number | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Quality inspections (migration 0092)
// ---------------------------------------------------------------------------

export interface InspectionItem {
  id: string;
  areaName: string;
  itemName: string;
  /** 1–5. Null = could not be accessed, which is NOT a zero and is not scored. */
  rating: number | null;
  comment: string | null;
  photoFileId: string | null;
}

export interface InspectionAction {
  id: string;
  inspectionId: string;
  inspectionItemId: string | null;
  description: string;
  dueOn: string | null;
  assignedUserId: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
}

/**
 * An outstanding action as the cross-walkthrough list shows it.
 *
 * The extra fields are the building it belongs to. Read on its own, "re-clean
 * the second floor and retrain the night crew" names no site and no client —
 * the one list whose entire job is to be chased needs to say what to chase.
 */
export interface OpenInspectionAction extends InspectionAction {
  customerId: string;
  /** Null when the client or the building has since been deleted. */
  customerName: string | null;
  siteName: string | null;
  /** The walkthrough's date, which is how old the complaint actually is. */
  scheduledFor: string;
}

/**
 * One scored walkthrough.
 *
 * `scoreBasisPoints` is snapshotted at completion, not derived on read: a
 * scorecard the client has been shown must not move because somebody edited
 * a line six weeks later.
 */
export interface Inspection {
  id: string;
  customerId: string;
  /**
   * Resolved for display, because a walkthrough is read by building rather
   * than by id. Null only where the office API is not what is being served —
   * the customer portal already knows whose scorecard it is showing.
   */
  customerName: string | null;
  propertyId: string;
  siteName: string | null;
  inspectorUserId: string | null;
  scheduledFor: string;
  startedAt: string | null;
  completedAt: string | null;
  status: InspectionStatus;
  scoreBasisPoints: number | null;
  scoredItems: number;
  internalNotes: string | null;
  customerNotes: string | null;
  sharedWithClient: boolean;
  /**
   * Optimistic concurrency token. Send the version last read with PATCH; a
   * stale save is refused rather than overwriting another supervisor's work.
   */
  version: number;
  items: InspectionItem[];
  actions: InspectionAction[];
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Supplies and compliance (migrations 0090, 0091)
// ---------------------------------------------------------------------------

/** What a building stocks, and how much of it it should have. */
export interface SupplyItem {
  id: string;
  propertyId: string;
  name: string;
  unit: string | null;
  /** The level "low" is measured against. Null = stocked, but unspecified. */
  parLevel: number | null;
  isBillable: boolean;
  unitPriceCents: number | null;
  isActive: boolean;
}

/** One crew member saying "this is low", and where it got to. */
export interface RestockRequest {
  id: string;
  propertyId: string;
  supplyItemId: string;
  /** The item's name, resolved for the list — this is read as prose. */
  itemName: string;
  appointmentId: string | null;
  requestedByUserId: string | null;
  quantity: number | null;
  note: string | null;
  status: RestockStatus;
  /** Snapshotted at delivery; null when the company absorbs the cost. */
  billedUnitPriceCents: number | null;
  billedCurrencyCode: string | null;
  monthlyInvoiceId: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

/**
 * Paperwork a client asks for before a crew is let in.
 *
 * `daysUntilExpiry` is derived on read rather than stored: it is a function
 * of today, and a stored copy would be wrong by definition the next morning.
 */
export interface ComplianceDocument {
  id: string;
  customerId: string | null;
  propertyId: string | null;
  kind: ComplianceDocumentKind;
  name: string;
  notes: string | null;
  fileId: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  expiryReminderSentOn: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Site access (migration 0089)
// ---------------------------------------------------------------------------

/**
 * One way into a building.
 *
 * There is deliberately no `secret` field. Revealing a code is its own
 * request against one credential, and the answer is audited — a list that
 * carried secrets would make that impossible to enforce and pointless to try.
 */
export interface SiteAccessCredential {
  id: string;
  propertyId: string;
  kind: SiteAccessKind;
  label: string;
  /** Whether there is a secret to reveal at all. False for a key or a fob. */
  hasSecret: boolean;
  instructions: string | null;
  holderUserId: string | null;
  /** When the secret last changed. Null = never rotated since it was recorded. */
  rotatedAt: string | null;
  rotationDueOn: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Change orders (migration 0088)
// ---------------------------------------------------------------------------

/**
 * Extra work a running contract asked for: one line, one approval, one more
 * line on the invoice the client was already going to pay.
 */
export interface ChangeOrder {
  id: string;
  customerId: string;
  customerName: string;
  propertyId: string | null;
  /** The building's name, where the order names one. */
  siteName: string | null;
  number: string;
  title: string;
  description: string | null;
  amountCents: number;
  currencyCode: string;
  isTaxable: boolean;
  serviceDate: string | null;
  status: ChangeOrderStatus;
  sentAt: string | null;
  sentToEmail: string | null;
  viewedAt: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  declinedAt: string | null;
  declinedByName: string | null;
  declineMessage: string | null;
  cancelledAt: string | null;
  /** The consolidated invoice this rode out on, once one carried it. */
  monthlyInvoiceId: string | null;
  createdAt: string;
  updatedAt: string;
  /** The visit it is added to (0104); null bills on the monthly invoice. */
  appointmentId: string | null;
  /** Enough of that visit to recognize it in a list. */
  visit: { scheduledDate: string; startTimeLocal: string; status: AppointmentStatus } | null;
  /** The saved add-on it was filled from. */
  addOnId: string | null;
  /** The office recorded the customer's agreement rather than the customer answering the link. */
  recordedByOffice: boolean;
  /** When the approved work went onto its visit's price. */
  visitAppliedAt: string | null;
  /** Why approved work could not go onto its visit; see EXTRA_WORK_VISIT_BLOCKED_REASONS. */
  visitBlockedReason: ExtraWorkVisitBlockedReason | null;
  /** Hand-typed work: whether its checklist task went onto the visit. */
  checklistTaskOutcome: 'added' | 'skipped' | null;
}

/**
 * One upcoming visit extra work could be added to
 * (`GET /change-orders/visit-options`). `blockedReason` says why one cannot
 * take it right now — it is already paid, priced from a range, and so on.
 */
export interface ExtraWorkVisitOption {
  id: string;
  scheduledDate: string;
  startTimeLocal: string;
  status: AppointmentStatus;
  propertyId: string | null;
  propertyAddress: string | null;
  priceCents: number | null;
  currencyCode: string | null;
  blockedReason: ExtraWorkVisitBlockedReason | null;
}

/**
 * What creating extra work, or recording the customer's agreement to it,
 * gives back. `checklistTasks` names the tasks that went onto (or could not
 * go onto) the visit's checklist when the work was added to a visit now.
 */
export interface ChangeOrderResult extends ChangeOrder {
  checklistTasks?: { added: string[]; skipped: string[] };
}

/**
 * What `POST /change-orders/:id/send` gives back.
 *
 * `approvalUrl` is returned ONCE, here, because only the token's hash is
 * stored: the office can paste it into their own email, and re-sending mints
 * a fresh link rather than recovering this one.
 */
export interface ChangeOrderSendResult {
  changeOrder: ChangeOrder;
  approvalUrl: string;
  /** Where the Worker mailed it, or null when it only returned the link. */
  emailedTo: string | null;
}

// ---------------------------------------------------------------------------
// Visit work order (GET /schedule/appointments/:id/worksheet)
// ---------------------------------------------------------------------------

export interface WorksheetTask {
  name: string;
  description: string | null;
  /**
   * The two texts above in the crew's other languages (see localized-text.ts). The work order
   * is the sharpest case for this: the sheet is printed from a quote written
   * in the customer's language and handed to whoever is cleaning.
   */
  nameTranslations: LocalizedText | null;
  descriptionTranslations: LocalizedText | null;
  estimatedMinutes: number;
  customerNotes: string | null;
  internalNotes: string | null;
}

export interface WorksheetRoom {
  roomName: string;
  /** The room's name in the crew's other languages (see localized-text.ts). */
  roomNameTranslations: LocalizedText | null;
  quantity: number;
  /**
   * The group this room was sold in. Null when the quote never named a group.
   * `groupQuantity` is how many copies of that group the visit covers — a
   * 3-bedroom apartment times forty is still one room row, with quantity 3
   * and groupQuantity 40.
   */
  groupName: string | null;
  groupNameTranslations: LocalizedText | null;
  groupQuantity: number;
  /**
   * Which group the heading belongs to. Two groups can share a name and a
   * quantity — two apartment stacks — and the worksheet must not merge them.
   */
  groupId: string | null;
  customerNotes: string | null;
  internalNotes: string | null;
  tasks: WorksheetTask[];
}

/**
 * Everything the person doing the visit needs on one page: the sold scope
 * (from the quote the schedule came from) and how to get in (from the
 * property record). `quoteNumber` is null when the series has no quote
 * provenance — the checklist simply is not available then.
 */
export interface VisitWorksheet {
  quoteNumber: string | null;
  rooms: WorksheetRoom[];
  property: {
    accessNotes: string | null;
    parkingNotes: string | null;
    petsDescription: string | null;
    /** The client's own code for this building, and who to ask for (0087). */
    siteCode: string | null;
    siteContactName: string | null;
    siteContactTitle: string | null;
    siteContactPhone: string | null;
  } | null;
  /**
   * The AI crew-briefing paragraph, cached per visit. Null when the assist
   * is off, the notes are empty, or generation failed — the structured
   * sections above carry everything either way.
   */
  briefing: string | null;
  /**
   * The visit's own checklist, copied the first time this worksheet is opened.
   * Empty when the property and the quote have no tasks.
   */
  checklist: VisitChecklist;
}

export interface PropertyChecklistItem {
  id: string;
  areaName: string;
  areaNameTranslations: LocalizedText | null;
  taskName: string;
  taskNameTranslations: LocalizedText | null;
  description: string | null;
  descriptionTranslations: LocalizedText | null;
  frequency: ServiceFrequency;
  requiresPhoto: boolean;
  displayOrder: number;
  sourceQuoteRoomServiceId: string | null;
}

export interface PropertyChecklist {
  enforceOrder?: boolean;
  id: string;
  propertyId: string;
  name: string;
  nameTranslations: LocalizedText | null;
  sourceQuoteId: string | null;
  version: number;
  items: PropertyChecklistItem[];
}

export interface VisitChecklistPhoto {
  id: string;
  fileId: string;
  downloadUrl: string;
}

export interface VisitChecklistItem {
  sectionKey?: string | null;
  layerName?: string | null;
  layerNameTranslations?: LocalizedText | null;
  estimatedMinutes?: number;
  taskRule?: import('../checklists/stack.ts').ChecklistDayRule;
  sectionRule?: import('../checklists/stack.ts').ChecklistDayRule;
  notApplicableApproved?: boolean;
  id: string;
  areaName: string;
  areaNameTranslations: LocalizedText | null;
  /** Same map as areaNameTranslations. The phone reads this name. */
  areaNameI18n: LocalizedText | null;
  taskName: string;
  taskNameTranslations: LocalizedText | null;
  /** Same map as taskNameTranslations. The phone reads this name. */
  taskNameI18n: LocalizedText | null;
  description: string | null;
  descriptionTranslations: LocalizedText | null;
  frequency: ServiceFrequency;
  requiresPhoto: boolean;
  displayOrder: number;
  status: ChecklistItemStatus;
  /** True when status is done. Skip and not applicable stay unticked. */
  ticked: boolean;
  /** False when this task's cadence says a different visit in the period does it. */
  due: boolean;
  /** True when due is false. The phone reads this name. */
  notDue: boolean;
  completedByUserId: string | null;
  completedByName: string | null;
  completedAt: string | null;
  /** Device time of the tick or untick that won. */
  tickedAt: string | null;
  note: string | null;
  clientUpdateId: string | null;
  updatedAt: string;
  photos: VisitChecklistPhoto[];
  /** File ids of the photos linked to this task. The phone reads this list. */
  photoIds: string[];
}

export interface VisitChecklist {
  revision?: number;
  definitionEditable?: boolean;
  scopeStartsAt?: string;
  occurrenceDate?: string;
  visitType?: import('../checklists/stack.ts').ChecklistVisitType | null;
  individuallyEdited?: boolean;
  photoRequirementsEnforced?: boolean;
  enforceOrder?: boolean;
  blockCompletionOnMissingPhotos?: boolean;
  missingRequiredPhotos?: number;
  /** False only inside the worksheet when this visit has nothing to copy. The checklist route answers 404 instead. */
  available: boolean;
  /** The phone hides the checkboxes when this is true. The office still uses its own permission. */
  readOnly: boolean;
  source: 'property' | 'quote' | 'none';
  progress: { completed: number; total: number };
  items: VisitChecklistItem[];
}

/** One visit's checklist as the office reads it back after the work. */
export interface VisitChecklistReport extends VisitChecklist {
  appointmentId: string;
  scheduledDate: string;
  startsAt: string;
  status: string;
  customerName: string;
}

// ---------------------------------------------------------------------------
// Global search (GET /search)
// ---------------------------------------------------------------------------

export interface SearchCustomerHit {
  id: string;
  /** The person's name, or the business name when there is no person. */
  name: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
}

export interface SearchPropertyHit {
  id: string;
  customerId: string;
  name: string | null;
  /** Street, city and state joined for display; null when the record has no address at all. */
  address: string | null;
  postalCode: string | null;
}

export interface SearchQuoteHit {
  id: string;
  quoteNumber: string;
  status: string;
  customerName: string;
  propertyAddress: string | null;
  totalCents: number;
  /** The quote's own currency. A company that switched still has old quotes in the old one. */
  currencyCode: string;
  quoteDate: string;
  /** As on a quote summary: a `range` quote's total is only the middle of its band. */
  pricePromise?: 'firm' | 'range' | null;
  priceRangeLowCents?: number | null;
  priceRangeHighCents?: number | null;
}

export interface SearchVisitHit {
  id: string;
  seriesId: string;
  status: string;
  scheduledDate: string;
  startTimeLocal: string;
  timezone: string;
  customerName: string;
  propertyAddress: string | null;
}

/**
 * What one search term found, by kind.
 *
 * Each kind is OPTIONAL rather than an empty array, and the difference
 * carries meaning: absent means the caller's role or the company's plan does
 * not cover that kind, present-and-empty means nothing matched. A client that
 * treats absent as empty renders a search that quietly says "no visits" to a
 * company that has never had scheduling — which is a different sentence.
 */
export interface SearchResults {
  query: string;
  customers?: SearchCustomerHit[];
  properties?: SearchPropertyHit[];
  quotes?: SearchQuoteHit[];
  visits?: SearchVisitHit[];
}

// ---------------------------------------------------------------------------
// Feature feedback board
// ---------------------------------------------------------------------------

/**
 * One idea, as a customer sees it.
 *
 * The submitting company is deliberately absent (§6). Somebody's business name
 * on a public board is a disclosure they never agreed to, and the board reads
 * exactly as well without it — `submittedByYourCompany` is the only thing a
 * reader needs, and it is only ever true for their own.
 */
export interface FeedbackRequestSummary {
  id: string;
  title: string;
  description: string;
  category: FeedbackCategory | null;
  status: FeedbackStatus;
  /** The simplified label from §16, already mapped for the client. */
  customerStatus: string;
  votes: number;
  /** How many distinct companies voted. Shown on completed items only. */
  companies: number;
  votesRecent: number;
  hasVoted: boolean;
  isFollowing: boolean;
  submittedByYourCompany: boolean;
  publicAdminResponse: string | null;
  previewAvailable: boolean;
  previewInstructions: string | null;
  previewQuestion: string | null;
  releaseVersion: string | null;
  releaseNotes: string | null;
  deployedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackStatusUpdate {
  status: FeedbackStatus;
  customerStatus: string;
  note: string | null;
  createdAt: string;
}

export interface FeedbackRequestDetail extends FeedbackRequestSummary {
  /** Public status notes, oldest first — the request's story so far. */
  timeline: FeedbackStatusUpdate[];
  /** Set when this request was merged; clients redirect to it. */
  canonicalRequestId: string | null;
  /** Titles of the requests folded into this one, for "also asked for as …". */
  mergedTitles: string[];
  /** The viewer's own preview response, when they left one. */
  previewResponse: { rating: string; feedback: string | null; createdAt: string } | null;
}

/** A candidate shown while somebody types a new title (§8). */
export interface FeedbackSimilarRequest {
  id: string;
  title: string;
  status: FeedbackStatus;
  customerStatus: string;
  votes: number;
  hasVoted: boolean;
  similarity: number;
}

export interface FeedbackEligibilityState {
  canSubmit: boolean;
  reason: string | null;
  weeklyLimit: number;
  weeklyUsed: number;
  weeklyRemaining: number;
  votesRequiredThisMonth: number;
  qualifyingVotesThisMonth: number;
  graceEndsAt: string | null;
}

export interface FeedbackBoardState {
  enabled: boolean;
  previewEnabled: boolean;
  /** Null when the board is switched off — there is no quota to describe. */
  eligibility: FeedbackEligibilityState | null;
  unreadNotifications: number;
}

export interface FeedbackNotification {
  id: string;
  requestId: string;
  requestTitle: string;
  event: string;
  title: string;
  body: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface FeedbackPreviewLaunch {
  url: string;
  expiresAt: string;
}

// --- In-app notifications (migration 0108) ----------------------------------

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  /** Web SPA path to deep-link to; null = nowhere better than the list. */
  linkPath: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

/** GET /notifications — one page of my feed plus the unbounded counts. */
export interface NotificationFeed {
  items: AppNotification[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** Unbounded, like the feedback badge — never derived from the page. */
  unread: number;
}

/** GET /notifications/state — the badge, cheap enough to poll. */
export interface NotificationState {
  unread: number;
}

// --- Operator console ------------------------------------------------------

export interface AdminFeedbackCounts {
  pending_moderation: number;
  approved: number;
  under_consideration: number;
  planned: number;
  in_progress: number;
  preview: number;
  deployed: number;
  rejected: number;
  merged: number;
  archived: number;
  possibleDuplicates: number;
  votesThisMonth: number;
}

export interface AdminFeedbackDuplicate {
  id: string;
  title: string;
  status: FeedbackStatus;
  votes: number;
  similarity: number;
}

export interface AdminFeedbackRow {
  id: string;
  title: string;
  description: string;
  category: FeedbackCategory | null;
  status: FeedbackStatus;
  companyId: string;
  companyName: string | null;
  submittedByUserId: string;
  submittedByName: string | null;
  submittedByEmail: string | null;
  votes: number;
  votesRecent7d: number;
  votesRecent30d: number;
  companiesVoting: number;
  /** Monthly recurring revenue of the companies that voted, in cents (§35). */
  voterMrrCents: number;
  createdAt: string;
  updatedAt: string;
  deployedAt: string | null;
  duplicateCount: number;
}

export interface AdminFeedbackDetail extends AdminFeedbackRow {
  canonicalRequestId: string | null;
  publicAdminResponse: string | null;
  previewPath: string | null;
  previewInstructions: string | null;
  previewQuestion: string | null;
  releaseVersion: string | null;
  releaseNotes: string | null;
  rejectionReason: string | null;
  approvedAt: string | null;
  githubRepository: string | null;
  githubIssueNumber: number | null;
  githubPullRequest: number | null;
  /** How the submitting company has been participating (§12). */
  submitter: {
    submissionsThisWeek: number;
    submissionsAllTime: number;
    votesAllTime: number;
    votesThisMonth: number;
    companyPlan: string | null;
    companyMrrCents: number;
    companyCreatedAt: string | null;
  };
  duplicates: AdminFeedbackDuplicate[];
  mergedInto: AdminFeedbackDuplicate[];
  statusHistory: Array<{
    id: string;
    oldStatus: FeedbackStatus | null;
    newStatus: FeedbackStatus;
    changedByEmail: string | null;
    publicNote: string | null;
    internalNote: string | null;
    createdAt: string;
  }>;
  moderationActions: Array<{
    id: string;
    action: string;
    moderatorEmail: string;
    reason: string | null;
    notes: string | null;
    customerMessage: string | null;
    targetRequestId: string | null;
    createdAt: string;
  }>;
  previewSessions: number;
  previewResponses: Array<{
    id: string;
    rating: string;
    feedback: string | null;
    createdAt: string;
  }>;
  /** Advisory model output, when duplicate detection is switched on (§32). */
  assistant: {
    available: boolean;
    suggestedCategory: FeedbackCategory | null;
    suggestedTitle: string | null;
    spamLikelihood: 'low' | 'medium' | 'high' | null;
    note: string | null;
  } | null;
}

export interface AdminFeedbackAnalytics {
  counts: AdminFeedbackCounts;
  votesThisMonth: number;
  votingCompanies: number;
  submittingCompanies: number;
  approvalRate: number;
  duplicateRate: number;
  averageVotesPerRequest: number;
  previewSessions: number;
  previewResponses: number;
  previewSatisfaction: number | null;
  shippedFromFeedback: number;
  medianDaysToShip: number | null;
  topRequests: AdminFeedbackRow[];
}

export interface AdminFeedbackSettings {
  enabled: boolean;
  weeklySubmissionLimit: number;
  voteRequirementEnabled: boolean;
  requiredVotesPerMonth: number;
  newCompanyGraceDays: number;
  moderationRequired: boolean;
  previewEnabled: boolean;
  previewSessionMinutes: number;
  aiDuplicateDetectionEnabled: boolean;
  /** Whether this deployment can actually open preview sessions. */
  previewConfigured: boolean;
  previewUrl: string | null;
}

export interface SavedTeam {
  id: string;
  locationId: string;
  name: string;
  color: string;
  leadUserId: string | null;
  isActive: boolean;
  revision: number;
  memberIds: string[];
}

// ---------------------------------------------------------------------------
// Margin-gated discounts (migration 0118)
// ---------------------------------------------------------------------------

export interface MarginFloorSettings {
  /** Whether the company's plan carries the margin floor (Pro). */
  available: boolean;
  floorBasisPoints: number;
  mode: 'off' | 'block' | 'override';
  /** What a task without its own loaded rate is costed at. Null = not set. */
  defaultLoadedHourlyCents: number | null;
}

export interface QuoteMarginLine {
  key: string;
  label: string;
  kind: 'room' | 'fee';
  revenueCents: number;
  discountCents: number;
  netRevenueCents: number;
  /** Cost amounts are null for a viewer who cannot see costs (no reports:read). */
  costCents: number | null;
  laborCostCents: number | null;
  materialsCents: number | null;
  travelCents: number | null;
  marginBasisPoints: number | null;
  belowFloor: boolean;
}

/** GET/POST /quotes/:id/margin — staff only, never on a customer page. */
export interface QuoteMarginView {
  quoteId: string;
  currencyCode: string;
  settings: MarginFloorSettings;
  discountCents: number;
  lowestMarginBasisPoints: number | null;
  belowFloor: boolean;
  costed: boolean;
  /** Whether the viewer may approve a below-floor discount themselves. */
  canApprove: boolean;
  /** The request for this quote still waiting on a manager, if any. */
  pendingOverrideId: string | null;
  lines: QuoteMarginLine[];
}

export interface QuoteMarginOverride {
  id: string;
  quoteId: string;
  quoteNumber: string | null;
  customerName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requestedBy: { id: string; name: string | null };
  decidedBy: { id: string; name: string | null } | null;
  decidedAt: string | null;
  reason: string;
  decisionNote: string | null;
  currencyCode: string;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  previousDiscountType: 'fixed' | 'percentage' | null;
  previousDiscountValue: number;
  discountCents: number;
  revenueCents: number;
  floorBasisPoints: number;
  lowestMarginBasisPoints: number | null;
  createdAt: string;
}

/** One salesperson's row in the override report. */
export interface MarginOverrideReportRow {
  userId: string;
  name: string | null;
  total: number;
  approved: number;
  rejected: number;
  pending: number;
  /** In `currencyCode`: a salesperson with discounts in two currencies has one row per currency. */
  discountCents: number;
  /** The currency the discounts in this row were recorded in. */
  currencyCode: string;
  /** The average lowest line margin across their requests, basis points. */
  averageLowestMarginBasisPoints: number | null;
}

export interface MarginOverrideReport {
  from: string;
  to: string;
  rows: MarginOverrideReportRow[];
}
